import {
    type ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import './_newPost.scss'
import 'swiper/css'

import { type AxiosError } from 'axios'
import { produce } from 'immer'
import _ from 'lodash'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useReactMediaRecorder } from 'react-media-recorder'
import { useQuery, useQueryClient } from 'react-query'
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import styled from 'styled-components'

import { addToast } from 'components/Toast/Toast'
import AudioMessage from 'components/UI/AudioMessage/AudioMessage'
import EditPreviewModal, {
    type ConfirmPayload,
    type StoryboardData
} from 'components/UI/Modal/EditPreviewModal/EditPreviewModal'
import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import AddGoal from 'features/AddGoal/AddGoal'
import AddPoll from 'features/AddPoll/AddPoll'
import { AudioRecorder } from 'features/MediaRecorder/AudioRecorder'
import MediaRecorder from 'features/MediaRecorder/MediaRecorder'
import PhotoEditView from 'features/PhotoEdit/PhotoEditView'
import { fastFileHash } from 'helpers/hash/fastFileHash'
import { useAudioRecorder } from 'helpers/hooks/useAudioRecorder/useAudioRecorder'
import { validateMediaFiles } from 'helpers/media'
import {
    getCachedBlob,
    prefetchImageToBlob
} from 'helpers/mediaPrefetch'
import {
  isPostQueryKey,
  updatePostInQueryData
} from 'helpers/post/updatePostInFeedCache'
import { randomString } from 'helpers/util'
import {
    type ClientFrame,
    type ClientThumbs,
    generateClientThumbs,
    revokeClientThumbs,
    validateClientThumbs
} from 'helpers/video/generateClientThumbs'
import PostModal from 'pages/postModal'
import {
    abortVideoMultipart,
    attachAudio,
    attachPhotoFromS3,
    attachPreviewCoverFromS3,
    attachVideoFromS3,
    checkPhotoDupe,
    checkVideoDupe,
    completeVideoMultipart,
    getVideoThumbs,
    presignPhoto,
    startVideoMultipart,
    uploadFileToS3,
    uploadVideoMultipart
} from 'services/endpoints/attachment'
import {
  editPoll,
  editPost,
  getSinglePostForEdit
} from 'services/endpoints/post'
import { getProfileGroups } from 'services/endpoints/social'
import { useUserProfile } from 'store'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'
import {
    type ICategory,
    type IGoal,
    type IGroup,
    type INewPost,
    type IPoll,
    type IProgressInfo,
    type ITag
} from 'types/interfaces/ITypes'

import { AddAnything } from './components/AddAnything'
import { useSortableProgress } from './components/Drag';
import { GoalPreview } from './components/GoalPreview/GoalPreview'
import { PollPreview } from './components/PollPreview'
import { PostImage } from './components/PostImage'
import { PostText } from './components/PostText'
import { PostVideo } from './components/PostVideo'
import { PostVisibility } from './components/PostVisibility'
import { PostInputs } from './features/PostInputs'
import { PostMediaInputs } from './features/PostMediaInputs'

interface IMediaToDelete {
    photos: string[]
    videos: string[]
    sounds: string[]
}

type EditPostLocationState = {
    editPost?: any
    fromSchedule?: boolean
    scheduleId?: string
}

export const EditPost = () => {
    const [pollData, setPollData] = useState<IPoll | null>(null)
    const [goalData, setGoalData] = useState<IGoal | null>(null)
    const { setTitle } = usePageTitle()
    const { setHideFooter } = useHeaderLayout()
    const emptyPost: INewPost = {
        body: '',
        groups: [],
        categories: [],
        tags: [],
        price: null,
        schedule_date: null,
        shareOnTwitter: false,
        photos: [],
        sounds: [],
        videos: []
    }

    const [post, setPost] = useState<INewPost>(emptyPost)
    const [postInitialState, setPostInitialState] =
        useState<INewPost>(emptyPost)
    const queryClient = useQueryClient()

    const [postAudience, setPostAudience] = useState<IGroup[]>([])
    const [selectedCategories, setSelectedCateories] = useState<ICategory[]>([])
    const [initialProgressInfos, setInitialProgressInfos] = useState<IProgressInfo[]>([])
    const [postType, setPostType] = useState<string>('')
    const [postDirty, setPostDirty] = useState<boolean>(false)

    const [selectedFiles, setSelectedFiles] = useState<File[]>([])

    const [progressInfosNew, setProgressInfosNew] = useState<{
        val: IProgressInfo[]
    }>({
        val: []
    })
    const [progressInfos, setProgressInfos] = useState<{
        val: IProgressInfo[]
    }>({
        val: []
    })
    const [mediaToDelete, setMediaToDelete] = useState<IMediaToDelete>({
        photos: [],
        videos: [],
        sounds: []
    })

    const [hasAudio, setHasAudio] = useState<boolean>(false)
    const [audioOnly, setAudioOnly] = useState<boolean>(false)

    const [recorderType, setRecorderType] = useState('')
    const [recordedChunks, setRecordedChunks] = useState([])
    const [postUploading, setPostUploading] = useState(false)
    const [reorderActive, setReorderActive] = useState<boolean>(false)

    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewMedia, setPreviewMedia] = useState<any>(null)

    const [addPollOpen, setAddPollOpen] = useState(false)
    const [addGoalOpen, setAddGoalOpen] = useState(false)

    const [editPreviewModalOpen, setEditPreviewModalOpen] = useState(false)

    type VideoPreviewData = {
        videoId: string
        defaultPreview: string
        storageSrc: string
        cloudfront?: CloudFrontCredentials | null
        customPreviewCloudfront?: CloudFrontCredentials | null
        duration?: number
        orientation?: string
        thumbnails?: string[]
        thumbnailTimes?: Array<number | null>
        scrubFrames?: ClientFrame[]
        thumbnailIndex?: number
        previewThumbnailIndex?: number | null
        customPreviewThumbKey?: string | null
        customPreviewThumbnailUrl?: string | null
        thumbsQueryDone?: boolean
        trimPreview?: { start: number; end: number }
        thumbType?: 'clip' | 'thumb'
        customThumbnailUrl?: string | null
        customThumbnailTime?: number | null
        customPreviewUrl?: string | null
        previewVideoId?: string | null
        storyboard?: StoryboardData | null
    }

    const fullThumbsHydratedRef = useRef(false)
    const thumbsHydrationInFlightRef = useRef(false)
    const [videoPreviewData, setVideoPreviewData] = useState<VideoPreviewData | null>(null);

    const updatePostCache =
    useProfileFeedCacheStore(
        state => state.updatePost
    )
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{ id: string }>()

    const postId = params.id ?? ''

    const locationState =
    location.state as EditPostLocationState | null

    const passedPost = locationState?.editPost

    const routePost =
    passedPost &&
    passedPost.id === postId
        ? passedPost
        : null

    const cameFromSchedule =
        locationState?.fromSchedule === true

    const scheduleId =
        locationState?.scheduleId ?? null


    const progressInfosRef = useRef<{
    val: IProgressInfo[]
    } | null>(null)

    const userData = useUserProfile()!
    const modalContext = useModalContext()
    const { t } = useTranslation()

    const postAudienceRef = useRef<IGroup[]>(postAudience);
    const priceRef = useRef<number | null>(post.price);
    const progressNextRef = useRef<IProgressInfo[] | null>(null);
    const syncRafRef = useRef<number | null>(null);
    const lastSortedIdsRef = useRef<string[]>([]);

    const uploadOpsRef = useRef<
    Record<
        string,
        {
        aborted: boolean
        controller: AbortController
        stage: 'idle' | 'presign' | 'hash' | 'dupe' | 'upload' | 'attach' | 'done'
        cancelCurrent?: () => void
        }
    >
    >({})

    const dropRef = useRef<HTMLDivElement>(null);

    const [selectedTaggedCreators, setSelectedTaggedCreators] = useState<any[]>([])
    const [tagCreatorsOpen, setTagCreatorsOpen] = useState(false)

    // USE STATE PASSED FROM EDIT POST MENU
    const buildExistingProgressInfos = useCallback(
    (
        sourcePost: any,
        thumbsData?: any,
    ): IProgressInfo[] => {
        const allMedia = [
        ...(sourcePost?.photos ?? []),
        ...(sourcePost?.videos ?? []),
        ...(sourcePost?.sounds ?? []),
        ]

        const sorted = allMedia
        .slice()
        .sort(
            (a: any, b: any) =>
            (a?.order ?? 0) - (b?.order ?? 0)
        )

        return sorted.map((item: any) => {
        const base: IProgressInfo = {
            percentage: 100,
            cancel: () => {},
            error: '',
            fileName: item.url ?? '',
            previewUrl: item.url ?? '',
            storageUrl:
            item.storage_path ??
            item.path ??
            item.url ??
            '',
            source: 'existing',
            locked: !!item.locked,
            orientation: item.orientation,

            thumbnails: [],
            imagePreview:
                item.type === 'photo'
                    ? item.thumb_url ?? item.url ?? undefined
                    : undefined,

            type: item.type,
            previewOn: !!item.preview,
            id: item.id,
            text: item.text,

            attachmentId:
            item.attachment_id ??
            item.video_id ??
            item.photo_id ??
            item.sound_id ??
            item.id ??
            undefined,

            duration:
            item.duration != null
                ? Number(item.duration)
                : undefined,

            thumbType:
            item.thumb_type ?? undefined,


            exif_video_media:
            item.exif_video_media ?? undefined,

            video_exif:
            item.video_exif ?? undefined,

            video_hash:
            item.video_hash ?? undefined,

            processing: false,
        }

        if (item?.type !== 'video') {
            return base
        }

        /*
        * The feed/single-post video already contains the active thumbnail.
        * Use it immediately instead of waiting for getVideoThumbs().
        */
        const activeImage: string | undefined =
            typeof item.active_thumb === 'string' &&
            item.active_thumb
                ? item.active_thumb
                : undefined

        let thumbnails: string[] = activeImage
            ? [activeImage]
            : []

        let thumbTimes: Array<number | null> = []

        const thumbRecord =
            thumbsData?.data?.data?.find(
                (entry: any) =>
                    String(entry?.id) === String(item.id)
            )
        const storyboard =
            thumbRecord?.storyboard ?? null

        if (Array.isArray(thumbRecord?.thumbs)) {
            const generatedThumbRows =
                thumbRecord.thumbs.filter(
                    (thumb: any) =>
                        !thumb?.type ||
                        thumb.type === 'thumb'
                )

            thumbTimes = generatedThumbRows.map(
                (thumb: any) =>
                    thumb?.time != null &&
                    Number.isFinite(Number(thumb.time))
                        ? Number(thumb.time)
                        : null
            )

            thumbnails = generatedThumbRows
                .map((thumb: any) => thumb?.src)
                .filter(
                    (src: unknown): src is string =>
                        typeof src === 'string' &&
                        src.length > 0
                )
        }
        const activeThumbnailIndex =
            activeImage && thumbnails.length
                ? thumbnails.indexOf(activeImage)
                : -1

        return {
            ...base,
            cloudfront: item.cloudfront ?? null,
            customPreviewCloudfront:
                item.customPreviewCloudfront ??
                item.preview_cloudfront ??
                item.preview?.cloudfront ??
                null,
            thumbnails,
            thumbTimes,
            storyboard,
            imagePreview: activeImage,
            activeThumbnailIndex:
                activeThumbnailIndex >= 0
                    ? activeThumbnailIndex
                    : null,

            originalActiveThumbnailIndex:
                activeThumbnailIndex >= 0
                    ? activeThumbnailIndex
                    : null,
            custom_thumb_key: item.custom_thumb_key ?? null,
            custom_thumb_crop: item.custom_thumb_crop ?? null,
            customThumbnailUrl:
                item.custom_thumb_url ??
                item.custom_thumbnail_url ??
                (item.custom_thumb_key ? activeImage ?? null : null),
            custom_thumbnail:
                item.custom_thumbnail != null &&
                Number.isFinite(Number(item.custom_thumbnail))
                    ? Number(item.custom_thumbnail)
                    : null,
            preview_video_id: item.preview_video_id ?? null,
            customPreviewUrl: item.custom_preview_url ?? null,
            previewThumbIndex:
                item.preview_thumb_index ??
                item.previewThumbIndex ??
                null,

            previewThumbKey:
                item.preview_thumb_key ??
                item.previewThumbKey ??
                null,

            previewThumbUrl:
                item.preview_thumb_url ??
                item.previewThumbUrl ??
                null,

            trimPreview:
                item.video_clip ?? null,

            thumbnailChanged: false,

            video_id: item.id ?? null,

            thumbTarget: thumbnails.length,
            thumbsDone: true,
            thumbsQueryDone: !!thumbsData,
            thumbs_from: thumbsData
                ? 'server'
                : 'initial-post',
        } satisfies IProgressInfo
        })
    },
    []
    )

    useEffect(() => {
    postAudienceRef.current = postAudience;
    }, [postAudience]);

    useEffect(() => {
    priceRef.current = post.price;
    }, [post.price]);

    useEffect(() => {
    return () => {
        if (syncRafRef.current != null) cancelAnimationFrame(syncRafRef.current);
    };
    }, []);


const { getId } = useSortableProgress({
    dropRef,
    itemCount: progressInfos.val.length,
    onSort: ids => {
        lastSortedIdsRef.current = ids

        setProgressInfos(prev => {
            const byId = new Map(
                prev.val.map(item => [
                    getId(item),
                    item,
                ])
            )

            const next = ids
                .map(id => byId.get(id))
                .filter(
                    (item): item is IProgressInfo =>
                        Boolean(item)
                )

            if (next.length !== prev.val.length) {
                const seen = new Set(
                    next.map(getId)
                )

                for (const item of prev.val) {
                    if (!seen.has(getId(item))) {
                        next.push(item)
                    }
                }
            }

            if (post.price) {
                const firstUnlockedIdx =
                    next.findIndex(
                        item => !item.locked
                    )

                if (firstUnlockedIdx > 0) {
                    const [unlockedItem] =
                        next.splice(
                            firstUnlockedIdx,
                            1
                        )

                    next.unshift(unlockedItem)
                }
            }

            progressInfosRef.current = {
                val: next,
            }

            setProgressInfosNew({
                val: next,
            })

            return {
                val: next,
            }
        })

        setSelectedFiles(prevFiles => {
            const pairs = progressInfos.val
                .map(
                    (progressInfo, index) =>
                        [
                            getId(progressInfo),
                            prevFiles[index],
                        ] as const
                )
                .filter(([, file]) => !!file)

            const byId = new Map<string, File>(
                pairs
            )

            const nextFiles = ids
                .map(id => byId.get(id))
                .filter(
                    (file): file is File =>
                        !!file
                )

            if (
                nextFiles.length !==
                prevFiles.length
            ) {
                const seen = new Set(nextFiles)

                for (const file of prevFiles) {
                    if (!seen.has(file)) {
                        nextFiles.push(file)
                    }
                }
            }

            return nextFiles
        })

        if (!postDirty) {
            setPostDirty(true)
        }
    },
})

    const { status, startRecording, stopRecording } = useReactMediaRecorder({
        audio: true,
        video: recorderType === 'video'
    })

    const {
        data: singlePostResponse,
        error,
    } = useQuery(
        ['getSinglePostForEdit', postId],
        async () => await getSinglePostForEdit(postId),
        {
            enabled: !!postId && !routePost,
            refetchOnWindowFocus: false,
        }
    )
    const fetchedPost = useMemo(() => {
    if (!singlePostResponse) {
        return null
    }

    return (
        (singlePostResponse)?.data?.data ??
        (singlePostResponse)?.data ??
        singlePostResponse
    )
    }, [singlePostResponse])

    const sourcePost = routePost ?? fetchedPost
    useEffect(() => {
        if (!sourcePost) {
            return
        }

        const photos = Array.isArray(sourcePost.photos)
            ? sourcePost.photos
            : []

        if (!photos.length) {
            return
        }

        photos.forEach((photo: any) => {
            const url =
                typeof photo?.url === 'string'
                    ? photo.url
                    : null

            if (!url) {
                return
            }

            void prefetchImageToBlob(url)
        })
    }, [sourcePost])
    const isScheduledPost = useMemo(() => {
        if (!sourcePost?.schedule_date) {
            return false
        }

        return new Date(sourcePost.schedule_date).getTime() > Date.now()
    }, [sourcePost?.schedule_date])
    const {
        startRecording: startAudioRecording,
        stopRecording: stopAudioRecording,
        audioFile,
        recording: audioRecording
    } = useAudioRecorder()
    const {
        data: groups = [],
        isLoading: isLoadingGroups,
    } = useQuery(
        'allGroups',
        getProfileGroups,
        {
            enabled: !!sourcePost && isScheduledPost,
            refetchOnWindowFocus: false,
            staleTime: 5000,
        }
    )

    if ((error as AxiosError)?.response?.status === 404) {
    navigate('/404', { replace: true })
    }

    const confirmationMessage = t('videoUploadInProgressVideoWillNotBeSaved')
    const beforeUnloadHandler = (e: any) => {
        ;(e || window.event).returnValue = confirmationMessage

        return confirmationMessage
    }

    useEffect(() => {
    if (editPreviewModalOpen) {
        setTitle(t('editPreview'))
        setHideFooter(true)
    } else {
        setTitle(t('editPost'))
        setHideFooter(false)
    }

    return () => {
        setHideFooter(false)
    }
    }, [editPreviewModalOpen, setTitle, setHideFooter, t])

const appendAttachments = useCallback(async (items: any[]) => {
  if (!items?.length) return

  const current = (
    progressInfosRef.current?.val ??
    progressInfos.val
  ) as any[]

  const initialItems = items.map((item: any) => {
    if (
      item?.type === 'video' &&
      typeof item.imagePreview === 'string' &&
      item.imagePreview.length > 0
    ) {
      return {
        ...item,
        source: 'vault',
        thumbnails: [item.imagePreview],
        activeThumbnailIndex: 0,
        thumbsQueryDone: false
      }
    }

    return {
        ...item,
        source: 'vault',
    }
  })

  const appendedItems = [...current, ...initialItems]

  // Attachment continues using item.imagePreview from ShareMediaModal.
  syncProgress(appendedItems)

  setSelectedFiles(previous => [
    ...previous,
    ...initialItems
  ])

  const videoIds = Array.from(
    new Set(
      items
        .filter((item: any) => item?.type === 'video')
        .map((item: any) =>
          item.video_id ?? item.id
        )
        .filter(
          (videoId): videoId is string =>
            typeof videoId === 'string' &&
            videoId.length > 0
        )
    )
  )

  if (!videoIds.length) return

  try {
    const response = await getVideoThumbs(videoIds)

    const rows =
      response?.data?.data ??
      response?.data ??
      []

    if (!Array.isArray(rows)) return

    const thumbsByVideoId = new Map<
        string,
        {
            src: string
            active?: boolean
            time?: number | null
            type?: string
        }[]
    >()

    const storyboardByVideoId = new Map<
        string,
        StoryboardData | null
    >()

    rows.forEach((row: any) => {
        const videoId =
            row?.id ?? row?.video_id

        if (
            typeof videoId !== 'string' ||
            !videoId.length ||
            !Array.isArray(row?.thumbs)
        ) {
            return
        }

        thumbsByVideoId.set(videoId, row.thumbs)
        storyboardByVideoId.set(
            videoId,
            row?.storyboard ?? null
        )
    })

    const latestItems = (
      progressInfosRef.current?.val ??
      appendedItems
    )

    const hydratedItems = latestItems.map(item => {
      if (item?.type !== 'video') {
        return item
      }

      const videoId =
        item.video_id ?? item.id

      if (
        typeof videoId !== 'string' ||
        !videoId.length
      ) {
        return item
      }

      const allThumbRows =
        thumbsByVideoId.get(videoId)

        const storyboard =
            storyboardByVideoId.get(videoId) ?? null

      if (!allThumbRows?.length) {
        return item
      }

      /*
       * Keep only the eight generated thumbnail rows here.
       * A custom thumbnail is passed separately to EditPreviewModal.
       */
      const generatedThumbRows = allThumbRows.filter(
        thumb =>
          !thumb?.type ||
          thumb.type === 'thumb'
      )

      const thumbnails = generatedThumbRows
        .map(thumb => thumb?.src)
        .filter(
          (src): src is string =>
            typeof src === 'string' &&
            src.length > 0
        )
    const thumbTimes = generatedThumbRows.map(
        thumb =>
            thumb?.time != null &&
            Number.isFinite(Number(thumb.time))
                ? Number(thumb.time)
                : null
    )
      const activeThumbnailIndex =
        generatedThumbRows.findIndex(
          thumb => thumb.active === true
        )

      const resolvedActiveIndex =
        activeThumbnailIndex >= 0
          ? activeThumbnailIndex
          : 0

        return {
            ...item,

            video_id: videoId,

            thumbnails,
            thumbTimes,
            storyboard,
            activeThumbnailIndex:
                item.customThumbnailUrl
                    ? null
                    : resolvedActiveIndex,

            imagePreview:
                item.imagePreview ??
                thumbnails[resolvedActiveIndex] ??
                thumbnails[0] ??
                null,

            thumbsQueryDone: true,
            thumbsDone: true,
            thumbTarget: thumbnails.length
        }
    })

    syncProgress(hydratedItems)

    setVideoPreviewData(previous => {
      if (!previous) {
        return previous
      }

      const hydratedVideo = hydratedItems.find(
        item =>
          item?.type === 'video' &&
          item.previewUrl === previous.defaultPreview
      )

      if (!hydratedVideo) {
        return previous
      }

        return {
            ...previous,
            thumbnails: hydratedVideo.thumbnails,
            thumbnailTimes:
                hydratedVideo.thumbTimes ??
                previous.thumbnailTimes,
            storyboard:
                hydratedVideo.storyboard ??
                previous.storyboard,
            thumbnailIndex:
                typeof hydratedVideo.activeThumbnailIndex === 'number'
                    ? hydratedVideo.activeThumbnailIndex
                    : previous.thumbnailIndex,
            thumbsQueryDone: true
        }
    })

    setSelectedFiles(hydratedItems)
  } catch (error) {
    console.error(
        '[EditPost] Failed to load Vault video thumbnails',
        error
    )
  }
}, [
  progressInfos.val,
  setSelectedFiles
])

const hydrateVideoThumbs = useCallback(
  async (sourcePost: any) => {
    if (
      fullThumbsHydratedRef.current ||
      thumbsHydrationInFlightRef.current
    ) {
      return
    }

    const videosArr = Array.isArray(sourcePost?.videos)
      ? sourcePost.videos
      : []

    const videoIds = videosArr
      .map((video: any) => video?.id)
      .filter(
        (id: string | undefined): id is string =>
          typeof id === 'string' &&
          id.length > 0
      )

    if (videoIds.length === 0) {
      fullThumbsHydratedRef.current = true
      return
    }

    thumbsHydrationInFlightRef.current = true

    try {
      const thumbsData = await getVideoThumbs(videoIds)

      const hydratedFromServer =
        buildExistingProgressInfos(
          sourcePost,
          thumbsData
        )

      const hydratedById = new Map(
        hydratedFromServer.map(item => [
          String(item.id),
          item,
        ])
      )

      const mergeThumbnailData = (
        current: IProgressInfo[]
      ): IProgressInfo[] =>
        current.map(item => {
          if (item.type !== 'video') {
            return item
          }

          const hydrated = hydratedById.get(
            String(item.id)
          )

          if (!hydrated) {
            return item
          }

          return {
            ...item,

            thumbnails:
              hydrated.thumbnails ?? [],
            thumbTimes:
                hydrated.thumbTimes ??
                item.thumbTimes ??
                [],
            storyboard:
                hydrated.storyboard ??
                item.storyboard ??
                null,  
            previewThumbIndex:
                (hydrated as any).previewThumbIndex ??
                (item as any).previewThumbIndex ??
                null,

            previewThumbKey:
                (hydrated as any).previewThumbKey ??
                (item as any).previewThumbKey ??
                null,

            previewThumbUrl:
                (hydrated as any).previewThumbUrl ??
                (item as any).previewThumbUrl ??
                null,

            trimPreview:
                (hydrated as any).trimPreview ??
                (item as any).trimPreview ??
                null,

            imagePreview:
              hydrated.imagePreview ??
              item.imagePreview,

            activeThumbnailIndex:
              hydrated.activeThumbnailIndex ??
              item.activeThumbnailIndex ??
              null,

            originalActiveThumbnailIndex:
              hydrated.originalActiveThumbnailIndex ??
              item.originalActiveThumbnailIndex ??
              null,

            customThumbnailUrl:
              hydrated.customThumbnailUrl ??
              item.customThumbnailUrl ??
              null,

            custom_thumb_key:
              (hydrated as any).custom_thumb_key ??
              (item as any).custom_thumb_key ??
              null,

            custom_thumb_crop:
              (hydrated as any).custom_thumb_crop ??
              (item as any).custom_thumb_crop ??
              null,

            preview_video_id:
              (hydrated as any).preview_video_id ??
              (item as any).preview_video_id ??
              null,

            customPreviewUrl:
              (hydrated as any).customPreviewUrl ??
              (item as any).customPreviewUrl ??
              null,

            thumbTarget:
              hydrated.thumbnails?.length ?? 0,

            thumbsDone: true,
            thumbsQueryDone: true,
            thumbs_from: 'server',
          }
        })

      const current =
        progressInfosRef.current?.val ?? []

      const hydratedCurrent =
        mergeThumbnailData(current)

      syncProgress(hydratedCurrent)

      setProgressInfosNew({
        val: hydratedCurrent,
      })

      setVideoPreviewData(previous => {
        if (!previous) {
          return previous
        }

        const hydratedVideo =
          hydratedCurrent.find(
            item =>
              item.type === 'video' &&
              item.previewUrl ===
                previous.defaultPreview
          )

        if (!hydratedVideo) {
          return previous
        }

        return {
          ...previous,

          thumbnails:
            hydratedVideo.thumbnails ?? [],
        thumbnailTimes:
            hydratedVideo.thumbTimes ??
            previous.thumbnailTimes,

            storyboard:
        hydratedVideo.storyboard ??
        previous.storyboard ??
        null,    
          thumbnailIndex:
            typeof hydratedVideo.activeThumbnailIndex ===
            'number'
              ? hydratedVideo.activeThumbnailIndex
              : previous.thumbnailIndex,

          customThumbnailUrl:
            hydratedVideo.customThumbnailUrl ??
            previous.customThumbnailUrl ??
            null,

          thumbsQueryDone: true,

            trimPreview:
                hydratedVideo.trimPreview ??
                previous.trimPreview,

            previewThumbnailIndex:
                hydratedVideo.previewThumbIndex ??
                previous.previewThumbnailIndex,

            customPreviewThumbKey:
                hydratedVideo.previewThumbKey ??
                previous.customPreviewThumbKey,

            customPreviewThumbnailUrl:
                hydratedVideo.previewThumbUrl ??
                previous.customPreviewThumbnailUrl,

            customPreviewUrl:
                hydratedVideo.customPreviewUrl ??
                previous.customPreviewUrl,

            previewVideoId:
                hydratedVideo.preview_video_id ??
                previous.previewVideoId,

        }
      })

      fullThumbsHydratedRef.current = true
    } catch (err) {
      console.warn(
        'getVideoThumbs failed:',
        err
      )
    } finally {
      thumbsHydrationInFlightRef.current = false
    }
  },
  [buildExistingProgressInfos]
)
useEffect(() => {
  fullThumbsHydratedRef.current = false
  thumbsHydrationInFlightRef.current = false
}, [postId])

useEffect(() => {
  if (!sourcePost) return

  const audienceFromPost: IGroup[] =
    Array.isArray(sourcePost.audience)
      ? sourcePost.audience
      : Array.isArray(sourcePost.groups)
        ? sourcePost.groups
        : []

  setPostAudience(audienceFromPost)

    const selectedForThisPost: ICategory[] = Array.isArray(
    sourcePost.media_categories
    )
    ? sourcePost.media_categories.map(
        (category: any) => ({
            ...category,

            id: String(category.id),
            name: String(category.name),

            public:
            category.public != null
                ? Number(category.public)
                : undefined,

            posts: Number(category.posts ?? 0),
            premium: Number(category.premium ?? 0),
            stories: Number(category.stories ?? 0),
            messages: Number(category.messages ?? 0),
        })
        )
    : []

    setSelectedCateories(selectedForThisPost)

  if (sourcePost.poll) {
    setPostType('poll')

    setPollData({
      type: sourcePost.poll.type,
      question: sourcePost.poll.question,
      pollBg:
        sourcePost.poll.background_color,
      pollBgImg:
        sourcePost.poll.background_src,
      answers:
        sourcePost.poll.answers_count?.map(
          (answer: any) => answer.text
        ) ?? [],
    })

    setGoalData(null)
  } else if (sourcePost.goal) {
    setPostType('goal')

    setGoalData({
      title: sourcePost.goal.text,
      amount: sourcePost.goal.goal,
      goalBg:
        sourcePost.goal.background_color,
      goalBgImg:
        sourcePost.goal.background_src,
    })

    setPollData(null)
  } else {
    setPostType('media')
    setPollData(null)
    setGoalData(null)

    const initialProgress =
        buildExistingProgressInfos(sourcePost)

    setInitialProgressInfos(initialProgress)

    setProgressInfos({
        val: initialProgress,
    })

    setProgressInfosNew({
        val: initialProgress,
    })

    progressInfosRef.current = {
      val: initialProgress,
    }

    /*
     * The active thumbnail is already displayed.
     * This only fetches the additional selectable thumbnails.
     */
    void hydrateVideoThumbs(sourcePost)
  }

  const normalizedTags = (
    sourcePost.tags ?? []
  ).map((tag: any) => ({
    start: tag.start,
    end: tag.end,
    user_id:
      tag.user_id ??
      tag.user?.id,
  }))

const initialPostState: INewPost = {
    body: sourcePost.body || '',
    groups: [],
    categories: [],
    tags: normalizedTags,
    price: sourcePost.price ?? null,
    schedule_date: sourcePost.schedule_date
        ? new Date(sourcePost.schedule_date)
        : null,
    shareOnTwitter: Boolean(sourcePost.shared_on_twitter),
    photos: [],
    sounds: [],
    videos: [],
}

  setPost(initialPostState)
  setPostInitialState(initialPostState)
}, [
  sourcePost,
  postId,
  buildExistingProgressInfos,
  hydrateVideoThumbs,
])

const applyDuplicateVideoData = (
  entry: any,
  data: any,
  fallback?: { duration?: number; orientation?: string; hash?: string }
) => {
  const d = data ?? {}
  const backendDuration = d.duration != null && Number.isFinite(Number(d.duration)) ? Number(d.duration) : null
  const thumbnails = Array.isArray(d.thumbnails) ? d.thumbnails : entry.thumbnails ?? []
  const activeIndex = typeof d.active_thumbnail === 'number'
    ? d.active_thumbnail
    : typeof d.active_thumbnail_index === 'number'
      ? d.active_thumbnail_index
      : 0

  Object.assign(entry, {
    type: 'video',
    processing: false,
    percentage: 100,
    error: '',
    locked: !!priceRef.current,
    storageUrl: d.storage_path ?? d.path ?? entry.storageUrl ?? null,
    video_id: d.video_id ?? d.id ?? entry.video_id ?? null,
    duration: backendDuration != null && backendDuration > 0
      ? backendDuration
      : fallback?.duration && fallback.duration > 0
        ? fallback.duration
        : entry.duration ?? null,
    orientation: d.original_orientation ?? d.orientation ?? fallback?.orientation ?? entry.orientation,
    hash_video: d.video_hash ?? fallback?.hash ?? entry.hash_video ?? null,
    exif_video_media: d.exif_media ?? null,
    thumbnails,
    activeThumbnailIndex: activeIndex,
    custom_thumb_key: d.custom_thumb_key ?? null,
    custom_thumb_crop: d.custom_thumb_crop ?? null,
    customThumbnailUrl:
      d.custom_thumb_url ??
      d.custom_thumbnail_url ??
      (d.custom_thumb_key ? d.active_thumb ?? null : null),
    preview_video_id: d.preview_video_id ?? null,
    customPreviewUrl: d.custom_preview_url ?? null,
    imagePreview: d.active_thumb ?? thumbnails[activeIndex] ?? thumbnails[0] ?? entry.imagePreview ?? null,
    thumbsDone: thumbnails.length > 0,
    thumbsQueryDone: true
  })
}


useEffect(() => {
    const hasUploadingMedia =
        post.photos.length > 0 ||
        post.videos.length > 0 ||
        post.sounds.length > 0

    if (!hasUploadingMedia) {
        return
    }

    window.addEventListener(
        'beforeunload',
        beforeUnloadHandler
    )

    return () => {
        window.removeEventListener(
            'beforeunload',
            beforeUnloadHandler
        )
    }
}, [
    post.photos.length,
    post.videos.length,
    post.sounds.length,
])

    useEffect(() => {
        const postChanged =
            !_.isEqual(post, postInitialState)

        const mediaChanged =
            !_.isEqual(
                progressInfosNew.val,
                initialProgressInfos
            )

        const hasDeletedMedia =
            mediaToDelete.photos.length > 0 ||
            mediaToDelete.videos.length > 0 ||
            mediaToDelete.sounds.length > 0

        setPostDirty(
            postChanged ||
            mediaChanged ||
            hasDeletedMedia
        )
    }, [
        post,
        postInitialState,
        progressInfosNew,
        initialProgressInfos,
        mediaToDelete,
    ])


    const postReady = useMemo(() => {
        const hasValidText = !!post.body?.trim();

        const isPhotoFinished = (f: IProgressInfo) =>
            f.type === 'photo' &&
            !!f.storageUrl &&
            !f.processing &&
            !f.error;

        const isVideoFinished = (f: IProgressInfo) =>
            f.type === 'video' &&
            (!!f.video_id || !!f.storageUrl) &&
            !f.processing &&
            !f.error;

        const isPV = (f: IProgressInfo) => f.type === 'photo' || f.type === 'video';
        const isPVFinished = (f: IProgressInfo) =>
            f.type === 'photo' ? isPhotoFinished(f) : isVideoFinished(f);

        const photoVideos = progressInfos.val.filter(isPV);
        const hasAnyPV = photoVideos.length > 0;
        const allPVFinished = photoVideos.every(isPVFinished);

        const hasAnyAttachment = progressInfos.val.length > 0;
        if (!hasValidText && !hasAnyAttachment && !pollData && !goalData) return false;

        if (hasAnyPV && !allPVFinished) return false;

        if (post.price && !photoVideos.some(isPVFinished)) return false;

        const hasAnyFinishedAttachment = progressInfos.val.some(f => {
            if (isPV(f)) return isPVFinished(f);
            if (f.type === 'audio') {
                return !!f.storageUrl && !f.error;
            }
            return false;
        });

        return hasValidText || hasAnyFinishedAttachment || !!pollData || !!goalData;
    }, [post.body, post.price, progressInfos.val, pollData, goalData]);

    useEffect(() => {
        if (audioFile) {
            setSelectedFiles([...selectedFiles, audioFile])
            uploadFiles([audioFile])
            !postDirty && setPostDirty(true)
        }
    
        // eslint-disable-next-line
    }, [audioFile])

    useEffect(() => {
        return () => {
            const list =
                progressInfosRef.current?.val ?? []

            for (const it of list) {
                revokeIfBlob(it?.previewUrl)
                revokeClientThumbAssets(it)
            }
        }
    }, [])

    useEffect(() => {
        const convertBlob = (blob: any) => {
            const file = new File([blob[0]], randomString(blob[0].type), {
                type: blob[0].type
            })
    
            setSelectedFiles([...selectedFiles, file])
            uploadFiles([file])
            !postDirty && setPostDirty(true)
        }
    
        if (recordedChunks.length > 0) convertBlob(recordedChunks)
    
        // eslint-disable-next-line
    }, [recordedChunks])

    const getPreviewStatus = (status: string) => {
        if (status === 'no-preview') {
            return false
        }
        return true
    }

    const handleConfirmReorder = () => {
    setReorderActive(false)

    if (!postDirty) {
        setPostDirty(true)
    }
    }

    const handleOpenCategories = () => {
    // Build the full catalog directly from the post resource when opening the modal
        const catalog: ICategory[] = (
        sourcePost?.media_categories ?? []
        ).map((category: any) => ({
        id: String(category.id),
        name: String(category.name),

        public:
            typeof category.public === 'number'
            ? category.public
            : undefined,

        posts: Array.isArray(category.posts)
            ? category.posts.length
            : 0,

        premium: Array.isArray(category.premium)
            ? category.premium.length
            : 0,

        stories: Array.isArray(category.stories)
            ? category.stories.length
            : 0,

        messages: Array.isArray(category.messages)
            ? category.messages.length
            : 0,
        }))

    modalContext.addModal(
        t('categories'),
        <SelectCategoriesModal
        title={t('categories')}
        categories={catalog}                 // full list from post payload
        selectedCategories={selectedCategories} // your existing state
        onCancel={handleCloseCategories}
        onSave={(selected) => {
            setSelectedCateories(selected)
            !postDirty && setPostDirty(true)
            modalContext.clearModal()
        }}
        />,
        true,
        true,
        'selectCategories'
    )
    }


    const handleCloseCategories = () => {
        modalContext.clearModal()
    }

    const selectFiles = async (event: ChangeEvent<HTMLInputElement>) => {
        !postDirty && setPostDirty(true)
    
        const tmpFiles = event.target.files
        if (!tmpFiles?.length) {
            return
        }
    
        const files = await validateMediaFiles(Array.from(tmpFiles), t)
        const audioFiles = files.filter(file => file.type.includes('audio/'))
        const prevAudioFiles = progressInfos.val.filter(
            info => info.type === 'sound' || info.type === 'audio'
        )
        const videoPhotoFiles = files.filter(file =>
            file.type.includes('video/') || file.type.includes('image/')
        )
        const tmpVideoPhotoFiles = videoPhotoFiles.filter(file => {
            const res = !selectedFiles.some(el => el.size === file.size)
            !res && addToast('error', t('error:cannotUploadDuplicateFile'))
            return res
        })
    
        if (
            audioFiles?.length > 1 ||
            (audioFiles?.length >= 1 && prevAudioFiles.length === 1)
        ) {
            addToast('error', t('youCanUploadOnlyOneAudioFilePerPost'))
        }
    
        if (files.length + progressInfos.val.length > 40) {
            addToast('error', t('maximumNumberOfMediaFilesExceeded'))
            return
        }
    
        if (audioFiles.length > 0) {
            if (prevAudioFiles.length === 1) {
                uploadFiles(tmpVideoPhotoFiles)
                setSelectedFiles(prev => [...prev, ...tmpVideoPhotoFiles])
            } else {
                uploadFiles([...tmpVideoPhotoFiles, audioFiles[0]])
                setSelectedFiles(prev => [...prev, ...tmpVideoPhotoFiles, audioFiles[0]])
            }
        } else {
            uploadFiles(tmpVideoPhotoFiles)
            setSelectedFiles(prev => [...prev, ...tmpVideoPhotoFiles])
        }
    
        setPostType('media')
    }
    
    const revokeIfBlob = (url?: string) => {
    if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch {}
    }
    };

    const removeMedia = (media: IProgressInfo) => {
    const clientId = String(media.id);

    abortUploadOp(clientId);
    revokeIfBlob(media?.previewUrl);

    revokeClientThumbAssets(media);

    const tmpProgressInfos = progressInfos.val.filter(
        (info: any) => info.id !== media.id
    );

    const tmpProgressInfosNew = progressInfosNew.val.filter(
        (info: any) => info.id !== media.id
    );

    setProgressInfos({ val: tmpProgressInfos })
    setProgressInfosNew({ val: tmpProgressInfosNew })
    progressInfosRef.current = { val: tmpProgressInfos }

    setSelectedFiles(prev =>
        prev.filter((_, idx) => {
        const item = progressInfos.val[idx];
        return item?.id !== media.id;
        })
    );

    if (tmpProgressInfos.length === 0 && tmpProgressInfosNew.length === 0) {
        setPostType('');
    }

    if (!postDirty) setPostDirty(true);
    };

    const removeByUrl = (url: string) => {
    const target = progressInfos.val.find(info => info.previewUrl === url);
    if (!target) return;

    abortUploadOp(String(target.id));
    revokeIfBlob(target.previewUrl);
    revokeClientThumbAssets(target);

    const tmpProgressInfos = progressInfos.val.filter((info: IProgressInfo) => {
        if (info.previewUrl === url) {
        const tmp: IMediaToDelete = { ...mediaToDelete };
        const isExistingPostMedia =
            info.source === 'existing' &&
            !info.client_id

        if (isExistingPostMedia && info.id && info.type === 'photo') {
            tmp.photos.push(String(info.id))
        }

        if (isExistingPostMedia && info.id && info.type === 'video') {
            tmp.videos.push(String(info.id))
        }

        if (
            isExistingPostMedia &&
            info.id &&
            (info.type === 'sound' || info.type === 'audio')
        ) {
            tmp.sounds.push(String(info.id))
        }
        setMediaToDelete(tmp);
        }
        return info.previewUrl !== url;
    });

    const tmpProgressInfosNew = progressInfosNew.val.filter(
        (info: IProgressInfo) => info.previewUrl !== url
    );

    if (tmpProgressInfos.length === 0 && tmpProgressInfosNew.length === 0) {
        setPostType('');
    }

    setProgressInfosNew({ val: [...tmpProgressInfosNew] });
    setProgressInfos({ val: [...tmpProgressInfos] });
    progressInfosRef.current = { val: [...tmpProgressInfos] };

    setSelectedFiles(prev =>
        prev.filter((_, idx) => {
        const item = progressInfos.val[idx];
        return item?.previewUrl !== url;
        })
    );

    if (!postDirty) setPostDirty(true);
    };

    const revokeClientThumbAssets = (entry: any) => {
        const thumbs: ClientFrame[] = Array.isArray(entry?.thumbnails)
            ? entry.thumbnails
                .map((url: unknown, index: number) => ({
                    url: typeof url === 'string' ? url : '',
                    time: Number(entry?.thumbTimes?.[index] ?? 0)
                }))
                .filter(
                    (frame: ClientFrame) =>
                        frame.url.startsWith('blob:')
                )
            : []

        const scrubFrames: ClientFrame[] =
            Array.isArray(entry?.scrubFrames)
                ? entry.scrubFrames.filter(
                    (frame: ClientFrame) =>
                        typeof frame?.url === 'string' &&
                        frame.url.startsWith('blob:')
                )
                : []

        revokeClientThumbs({
            thumbs,
            scrubFrames
        })
    }

    const applyGeneratedClientThumbs = (
        entry: any,
        generated: ClientThumbs
    ) => {
        const thumbnails = generated.thumbs.map(
            frame => frame.url
        )

        const thumbTimes = generated.thumbs.map(
            frame => frame.time
        )

        const currentIndex =
            typeof entry.activeThumbnailIndex === 'number' &&
            entry.activeThumbnailIndex >= 0 &&
            entry.activeThumbnailIndex < thumbnails.length
                ? entry.activeThumbnailIndex
                : 0

        entry.thumbnails = thumbnails
        entry.thumbTimes = thumbTimes
        entry.scrubFrames = generated.scrubFrames
        entry.thumbTarget = thumbnails.length
        entry.thumbsDone = thumbnails.length > 0
        entry.thumbsQueryDone = thumbnails.length > 0
        entry.thumbs_from = 'client'

        if (
            !entry.customThumbnailUrl &&
            entry.custom_thumbnail == null &&
            thumbnails.length
        ) {
            entry.activeThumbnailIndex = currentIndex
            entry.imagePreview =
                thumbnails[currentIndex] ??
                thumbnails[0] ??
                entry.imagePreview ??
                null
        }
    }
    const syncProgress = (next: IProgressInfo[]) => {
    if (lastSortedIdsRef.current.length) {
        const byId = new Map(next.map(it => [getId(it), it]));
        const ordered = lastSortedIdsRef.current
        .map(id => byId.get(id))
        .filter(Boolean) as IProgressInfo[];

        for (const it of next) {
        if (!ordered.includes(it)) ordered.push(it);
        }

        next = ordered;
    }

    progressNextRef.current = next;

    if (syncRafRef.current != null) return;

    syncRafRef.current = requestAnimationFrame(() => {
        syncRafRef.current = null;

        const finalNext = progressNextRef.current ?? [];
        progressNextRef.current = null;

        setProgressInfos({ val: finalNext });
        progressInfosRef.current = { val: finalNext };
    });
    };

    const ensureUploadOp = (clientId: string) => {
    if (!uploadOpsRef.current[clientId]) {
        uploadOpsRef.current[clientId] = {
        aborted: false,
        controller: new AbortController(),
        stage: 'idle',
        cancelCurrent: undefined,
        };
    }
    return uploadOpsRef.current[clientId];
    };

    const setUploadStage = (
    clientId: string,
    stage: NonNullable<typeof uploadOpsRef.current[string]>['stage']
    ) => {
    const op = ensureUploadOp(clientId);
    op.stage = stage;
    };

    const abortUploadOp = (clientId: string) => {
    const op = uploadOpsRef.current[clientId];
    if (!op) return;

    op.aborted = true;

    try {
        op.cancelCurrent?.();
    } catch {}

    try {
        op.controller.abort();
    } catch {}
    };

    const clearUploadOp = (clientId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete uploadOpsRef.current[clientId];
    };

    const isUploadAborted = (clientId: string) => {
    return !!uploadOpsRef.current[clientId]?.aborted;
    };

    const throwIfUploadAborted = (clientId: string) => {
    if (isUploadAborted(clientId)) {
        const err = new Error('__UPLOAD_ABORTED__');
        (err as any).code = '__UPLOAD_ABORTED__';
        throw err;
    }
    };

    const upload = useCallback(
    async (file: File, clientId: string) => {
        let _progressInfos = [...(progressInfosRef.current?.val ?? [])];
        const data = new FormData();

        const op = ensureUploadOp(clientId)
        const signal = op.controller.signal

        const safeSyncById = (
        updater: (entry: any) => void,
        fallback?: (list: any[]) => void
        ) => {
        if (isUploadAborted(clientId)) return;

        const list = [...(progressInfosRef.current?.val ?? [])];
        const index = list.findIndex(item => item.id === clientId);

        if (index >= 0) {
            updater(list[index]);
            syncProgress(list);
        } else if (fallback) {
            fallback(list);
        }
        };

        try {
        // -------------------------
        // PHOTO
        // -------------------------
        if (file.type.includes('image')) {
            setUploadStage(clientId, 'presign');
            const { url: putUrl, key } = await presignPhoto(
            {
                type: 'post_image',
                contentType: file.type,
                size: file.size
            },
            signal
            );
            throwIfUploadAborted(clientId);

            setUploadStage(clientId, 'hash');
            const fileHash = await fastFileHash(file);
            throwIfUploadAborted(clientId);

            setUploadStage(clientId, 'dupe');
            const group_ids = extractGroupIds(postAudienceRef.current);
            const dupe = await checkPhotoDupe(
            {
                h: fileHash,
                size: file.size,
                group_ids,
            },
            signal
            );
            throwIfUploadAborted(clientId);

        if (dupe?.status === 'OK') {
            safeSyncById(entry => {
                entry.photo_id = dupe.data.id
                entry.storageUrl =
                    dupe.data.path ??
                    dupe.data.storage_path

                entry.status = 'finished'
                entry.exif_media =
                    dupe.data.exif_media ?? null

                entry.orientation =
                    dupe.data.original_orientation ??
                    entry.orientation

                entry.processing = false
                entry.percentage = 100
                entry.locked = !!priceRef.current

                entry.hash_image =
                    dupe.data.image_hash ??
                    fileHash

                entry.is_duplicate = true
            })

            setUploadStage(clientId, 'done')
            clearUploadOp(clientId)

            return null
        }

            setUploadStage(clientId, 'upload');
            await uploadFileToS3(
            putUrl,
            file,
            (loaded, total, cancelCb) => {
                const op = ensureUploadOp(clientId);
                op.cancelCurrent = cancelCb;

                if (isUploadAborted(clientId)) {
                try { cancelCb?.(); } catch {}
                return;
                }

                const list = [...(progressInfosRef.current?.val ?? [])];
                const index = list.findIndex(item => item.id === clientId);
                if (index >= 0) {
                const it = list[index];
                const perc = Math.round(
                    (loaded * 100) / (total || file.size || 1)
                );
                it.percentage = perc;
                it.cancel = cancelCb;
                it.error = '';
                it.type = 'photo';
                it.processing = true;
                syncProgress(list);
                }
            },
            signal
            );
            throwIfUploadAborted(clientId);

            setUploadStage(clientId, 'attach');
            const attach = await attachPhotoFromS3(
            {
                s3_key: key,
                type: 'post',
                hash_image: fileHash,
            },
            signal
            ).catch(err => {
            console.error('[upload] attachPhotoFromS3 failed', err);
            throw err;
            });
            throwIfUploadAborted(clientId);

            const resData = attach?.data?.data ?? attach?.data ?? attach;

            safeSyncById((entry) => {
                entry.storageUrl =
                    resData?.storage_path ??
                    resData?.path ??
                    key;

                entry.photo_id =
                    resData?.id ??
                    entry.photo_id ??
                    null;

                entry.locked = !!priceRef.current;
                entry.exif_data = resData?.exif_data;
                entry.hash_image = fileHash;
                entry.exif_media = resData?.exif_media;
                entry.orientation =
                    resData?.original_orientation ??
                    entry.orientation;
                entry.processing = false;
                entry.percentage = 100;
            });

            setUploadStage(clientId, 'done');
            clearUploadOp(clientId);
            return attach;
        }

        // -------------------------
        // VIDEO
        // -------------------------
        if (file.type.includes('video')) {
            setUploadStage(clientId, 'hash')

            const [meta, fileHash] = await Promise.all([
                import('helpers/video/readClientMeta')
                    .then(
                        async ({ readClientVideoMeta }) =>
                            await readClientVideoMeta(file)
                    )
                    .catch(() => null),
                fastFileHash(file)
            ])

            const duration = meta?.duration || 0
            const orientation =
                meta?.orientation as
                    | 'Landscape'
                    | 'Portrait'
                    | undefined

            throwIfUploadAborted(clientId)

            setUploadStage(clientId, 'dupe')

            const dupe = await checkVideoDupe(
                {
                    h: fileHash,
                    size: file.size,
                    group_ids:
                        extractGroupIds(
                            postAudienceRef.current
                        )
                },
                signal
            )

            throwIfUploadAborted(clientId)

            /*
            * --------------------------------------------------
            * DUPLICATE VIDEO
            * --------------------------------------------------
            *
            * Existing backend thumbs remain available, but we
            * also generate client thumbs so the newly added
            * video gets the same local editing/scrubbing
            * experience as a normal upload.
            */
            if (dupe?.status === 'OK') {
                safeSyncById(entry => {
                    applyDuplicateVideoData(
                        entry,
                        dupe.data,
                        {
                            duration,
                            orientation,
                            hash: fileHash
                        }
                    )

                    revokeClientThumbAssets(entry)
                    entry.thumbnails = []
                    entry.thumbTimes = []
                    entry.scrubFrames = []
                    entry.thumbTarget = 0
                    entry.thumbsDone = false
                    entry.thumbsQueryDone = false
                    entry.thumbs_from = 'client'
                })

                try {
                    const generated =
                        await generateClientThumbs(
                            file,
                            duration
                        )

                    throwIfUploadAborted(clientId)

                    const expectedThumbCount =
                        duration > 0
                            ? Math.min(
                                8,
                                Math.max(
                                    1,
                                    Math.floor(duration)
                                )
                            )
                            : generated.thumbs.length

                    const valid =
                        await validateClientThumbs(
                            generated,
                            expectedThumbCount
                        )

                    throwIfUploadAborted(clientId)

                    console.log(
                        '[CLIENT THUMBS] duplicate validation',
                        {
                            valid,
                            thumbs:
                                generated.thumbs.length,
                            scrubFrames:
                                generated.scrubFrames.length
                        }
                    )

                    if (valid) {
                        safeSyncById(entry => {
                            revokeClientThumbAssets(entry)

                            applyGeneratedClientThumbs(
                                entry,
                                generated
                            )
                        })
                    } else {
                        revokeClientThumbs(
                            generated
                        )
                    }
                } catch (error) {
                    console.warn(
                        '[EditPost] Duplicate client thumbnail generation failed; using server thumbs',
                        error
                    )
                }

                throwIfUploadAborted(clientId)

                safeSyncById(entry => {
                    const hasThumbs =
                        Array.isArray(
                            entry.thumbnails
                        ) &&
                        entry.thumbnails.length > 0

                    entry.processing = false
                    entry.uploading = false
                    entry.percentage = 100
                    entry.error = ''

                    if (
                        entry.thumbs_from ===
                        'server'
                    ) {
                        entry.thumbsDone =
                            hasThumbs
                        entry.thumbsQueryDone =
                            hasThumbs
                    }
                })

                setUploadStage(
                    clientId,
                    'done'
                )

                clearUploadOp(clientId)

                return null
            }

            /*
            * --------------------------------------------------
            * NEW VIDEO
            * --------------------------------------------------
            */

            const advancedPreview = true

            setUploadStage(
                clientId,
                'presign'
            )

            const multipart =
                await startVideoMultipart(
                    {
                        type: 'post_video',
                        contentType: file.type,
                        size: file.size
                    },
                    signal
                )

            throwIfUploadAborted(clientId)

            const {
                key,
                uploadId,
                partSize,
                totalParts,
                parts
            } = multipart

            const uploadController =
                new AbortController()

            let multipartAborted = false

            const cleanupMultipart =
                async () => {
                    if (multipartAborted) {
                        return
                    }

                    multipartAborted = true

                    try {
                        uploadController.abort()
                    } catch {}

                    try {
                        await abortVideoMultipart({
                            key,
                            uploadId
                        })
                    } catch (error) {
                        console.error(
                            '[EditPost] Failed to abort multipart upload',
                            error
                        )
                    }
                }

            const handleOuterAbort =
                () => {
                    void cleanupMultipart()
                }

            if (signal.aborted) {
                await cleanupMultipart()
                throwIfUploadAborted(clientId)
            }

            signal.addEventListener(
                'abort',
                handleOuterAbort,
                { once: true }
            )

            try {
                /*
                * Start the S3 upload first.
                *
                * Client thumbnail generation runs in
                * parallel and therefore does not delay
                * the upload start.
                */
                setUploadStage(
                    clientId,
                    'upload'
                )

                const uploadPromise =
                    uploadVideoMultipart({
                        file,
                        key,
                        uploadId,
                        partSize,
                        totalParts,
                        parts,
                        contentType: file.type,
                        concurrency: 4,
                        signal:
                            uploadController.signal,

                        setProgress:
                            (
                                loaded,
                                total
                            ) => {
                                const op =
                                    ensureUploadOp(
                                        clientId
                                    )

                                const cancelUpload =
                                    () => {
                                        void cleanupMultipart()
                                    }

                                op.cancelCurrent =
                                    cancelUpload

                                if (
                                    isUploadAborted(
                                        clientId
                                    )
                                ) {
                                    void cleanupMultipart()
                                    return
                                }

                                const list = [
                                    ...(
                                        progressInfosRef
                                            .current?.val ??
                                        []
                                    )
                                ]

                                const index =
                                    list.findIndex(
                                        item =>
                                            item.id ===
                                            clientId
                                    )

                                if (index < 0) {
                                    return
                                }

                                const entry =
                                    list[index]

                                entry.percentage =
                                    Math.round(
                                        (
                                            loaded *
                                            100
                                        ) /
                                        (
                                            total ||
                                            file.size ||
                                            1
                                        )
                                    )

                                entry.cancel =
                                    cancelUpload

                                entry.error = ''
                                entry.type = 'video'

                                entry.previewOn =
                                    getPreviewStatus(
                                        userData
                                            .post_preview_type
                                    )

                                entry.processing = true
                                entry.uploading = true

                                syncProgress(list)
                            }
                    })

                /*
                * Generate + validate client thumbs
                * independently and in parallel with
                * the S3 upload.
                */
                const clientThumbsPromise =
                    generateClientThumbs(
                        file,
                        duration
                    )
                        .then(
                            async generated => {
                                if (
                                    isUploadAborted(
                                        clientId
                                    )
                                ) {
                                    revokeClientThumbs(
                                        generated
                                    )

                                    return {
                                        generated:
                                            null as
                                            | ClientThumbs
                                            | null,
                                        valid: false,
                                        error:
                                            null as unknown
                                    }
                                }

                                const expectedThumbCount =
                                    duration > 0
                                        ? Math.min(
                                            8,
                                            Math.max(
                                                1,
                                                Math.floor(
                                                    duration
                                                )
                                            )
                                        )
                                        : generated
                                            .thumbs
                                            .length

                                const valid =
                                    await validateClientThumbs(
                                        generated,
                                        expectedThumbCount
                                    )

                                if (
                                    isUploadAborted(
                                        clientId
                                    )
                                ) {
                                    revokeClientThumbs(
                                        generated
                                    )

                                    return {
                                        generated:
                                            null as
                                            | ClientThumbs
                                            | null,
                                        valid: false,
                                        error:
                                            null as unknown
                                    }
                                }

                                console.log(
                                    '[CLIENT THUMBS] validation',
                                    {
                                        valid,
                                        expectedThumbCount,
                                        thumbs:
                                            generated
                                                .thumbs
                                                .length,
                                        scrubFrames:
                                            generated
                                                .scrubFrames
                                                .length
                                    }
                                )

                                if (!valid) {
                                    revokeClientThumbs(
                                        generated
                                    )

                                    return {
                                        generated:
                                            null as
                                            | ClientThumbs
                                            | null,
                                        valid: false,
                                        error:
                                            null as unknown
                                    }
                                }

                                /*
                                * Show the client thumbs immediately
                                * while the actual video upload/attach
                                * is still running.
                                */
                                safeSyncById(
                                    entry => {
                                        revokeClientThumbAssets(
                                            entry
                                        )

                                        applyGeneratedClientThumbs(
                                            entry,
                                            generated
                                        )

                                        entry.processing =
                                            true
                                    }
                                )

                                return {
                                    generated,
                                    valid: true,
                                    error:
                                        null as unknown
                                }
                            }
                        )
                        .catch(error => {
                            console.error(
                                '[EditPost] Client thumbnail generation failed',
                                error
                            )

                            return {
                                generated:
                                    null as
                                    | ClientThumbs
                                    | null,
                                valid: false,
                                error
                            }
                        })

                /*
                * Wait for S3 upload.
                */
                const completedParts =
                    await uploadPromise

                throwIfUploadAborted(
                    clientId
                )

                if (
                    !completedParts?.length
                ) {
                    throw new Error(
                        'Multipart video upload did not complete.'
                    )
                }

                await completeVideoMultipart(
                    {
                        key,
                        uploadId,
                        parts:
                            completedParts
                    },
                    signal
                )

                throwIfUploadAborted(
                    clientId
                )

                /*
                * We need the client-thumb result before
                * attach because attachVideoFromS3 needs
                * client_thumbs_ready.
                */
                const clientThumbs =
                    await clientThumbsPromise

                throwIfUploadAborted(
                    clientId
                )

                const clientThumbsReady =
                    clientThumbs.valid &&
                    clientThumbs.generated !==
                        null

                setUploadStage(
                    clientId,
                    'attach'
                )

                const attach =
                    await attachVideoFromS3(
                        {
                            s3_key: key,

                            duration:
                                duration > 0
                                    ? Number(
                                        duration.toFixed(
                                            2
                                        )
                                    )
                                    : null,

                            orientation:
                                orientation ??
                                null,

                            video_hash:
                                fileHash,

                            preview:
                                getPreviewStatus(
                                    userData
                                        .post_preview_type
                                ),

                            advanced_preview:
                                advancedPreview,

                            client_thumbs_ready:
                                clientThumbsReady,

                            type: 'post'
                        } as any,
                        signal
                    )

                throwIfUploadAborted(
                    clientId
                )

                const resData =
                    attach?.data?.data ??
                    attach?.data ??
                    attach

                /*
                * Basic attached-video fields.
                */
                safeSyncById(
                    entry => {
                        entry.type = 'video'

                        entry.storageUrl =
                            resData?.path ??
                            key

                        entry.video_id =
                            resData?.id ??
                            entry.video_id ??
                            null

                        entry.duration =
                            (
                                typeof resData
                                    ?.duration ===
                                'number'
                                    ? resData.duration
                                    : duration
                            ) ??
                            entry.duration

                        entry.orientation =
                            resData?.orientation ??
                            orientation ??
                            entry.orientation

                        entry.locked =
                            !!priceRef.current

                        entry.hash_video =
                            fileHash

                        entry.exif_video_media =
                            resData
                                ?.exif_video_media ??
                            null

                        entry.uploading = false
                        entry.percentage = 100
                        entry.processing = true
                        entry.error = ''
                    }
                )

                throwIfUploadAborted(
                    clientId
                )

                /*
                * --------------------------------------------------
                * CLIENT THUMBS WON
                * --------------------------------------------------
                */
                if (
                    clientThumbsReady &&
                    clientThumbs.generated
                ) {
                    safeSyncById(
                        entry => {
                            applyGeneratedClientThumbs(
                                entry,
                                clientThumbs.generated as ClientThumbs
                            )

                            entry.processing = false
                            entry.uploading = false
                            entry.percentage = 100
                            entry.error = ''
                        }
                    )
                } else {
                    /*
                    * --------------------------------------------------
                    * SERVER THUMBS WON
                    * --------------------------------------------------
                    */
                    const serverThumbnails =
                        Array.isArray(
                            resData?.thumbnails
                        )
                            ? resData.thumbnails.filter(
                                (
                                    url: unknown
                                ): url is string =>
                                    typeof url ===
                                        'string' &&
                                    url.length > 0
                            )
                            : []

                    const serverThumbnailTimes =
                        Array.isArray(
                            resData
                                ?.thumbnail_times
                        )
                            ? resData.thumbnail_times
                            : Array.isArray(
                                resData
                                    ?.thumbnailTimes
                            )
                                ? resData.thumbnailTimes
                                : []

                    const requestedActiveIndex =
                        typeof resData
                            ?.active_thumbnail ===
                        'number'
                            ? resData
                                .active_thumbnail
                            : 0

                    const activeIndex =
                        requestedActiveIndex >=
                            0 &&
                        requestedActiveIndex <
                            serverThumbnails.length
                            ? requestedActiveIndex
                            : 0

                    safeSyncById(
                        entry => {
                            revokeClientThumbAssets(
                                entry
                            )

                            entry.thumbnails =
                                serverThumbnails

                            entry.thumbTimes =
                                serverThumbnailTimes

                            entry.scrubFrames = []

                            entry.thumbTarget =
                                serverThumbnails.length

                            entry.thumbsDone =
                                true

                            entry.thumbsQueryDone =
                                true

                            entry.thumbs_from =
                                'server'

                            entry.activeThumbnailIndex =
                                activeIndex

                            entry.imagePreview =
                                resData
                                    ?.active_thumb ??
                                serverThumbnails[
                                    activeIndex
                                ] ??
                                serverThumbnails[0] ??
                                entry.imagePreview ??
                                null

                            entry.processing = false
                            entry.uploading = false
                            entry.percentage = 100
                            entry.error = ''
                        }
                    )
                }

                setUploadStage(
                    clientId,
                    'done'
                )

                clearUploadOp(
                    clientId
                )

                return attach
            } catch (error) {
                await cleanupMultipart()
                throw error
            } finally {
                signal.removeEventListener(
                    'abort',
                    handleOuterAbort
                )
            }
        }
        // -------------------------
        // AUDIO
        // -------------------------
        if (file.type.includes('audio')) {
            setUploadStage(clientId, 'upload');
            data.append('audio', file);

            const res = await attachAudio(
            data,
            (loaded: number, total: number, cancelCb: () => void) => {
                const op = ensureUploadOp(clientId);
                op.cancelCurrent = cancelCb;

                if (isUploadAborted(clientId)) {
                try { cancelCb?.(); } catch {}
                return;
                }

                _progressInfos = [...(progressInfosRef.current?.val ?? [])];
                const index = _progressInfos.findIndex(item => item.id === clientId);

                if (index >= 0) {
                _progressInfos[index].percentage = Math.round((100 * loaded) / total);
                _progressInfos[index].cancel = cancelCb;
                _progressInfos[index].error = '';
                _progressInfos[index].type = 'audio';
                }

                syncProgress(_progressInfos);
            },
            signal
            );
            throwIfUploadAborted(clientId);

            if (res?.data?.data?.path) {
            const resData = res.data.data;
            _progressInfos = [...(progressInfosRef.current?.val ?? [])];

            const index = _progressInfos.findIndex(item => item.id === clientId);

            if (index >= 0 && !isUploadAborted(clientId)) {
                _progressInfos[index].previewUrl = resData.path;
                _progressInfos[index].storageUrl = resData.storage_path;
                _progressInfos[index].locked = !!priceRef.current;

                syncProgress(_progressInfos);
            }
            }

            setUploadStage(clientId, 'done');
            clearUploadOp(clientId);
            return res;
        }

        clearUploadOp(clientId);
        return null;
        } catch (err: any) {
            if (
            err?.code === '__UPLOAD_ABORTED__' ||
            err?.message === '__UPLOAD_ABORTED__' ||
            err?.name === 'AbortError' ||
            err?.code === 'ERR_CANCELED'
            ) {
            clearUploadOp(clientId);
            return null;
            }

        const list = [...(progressInfosRef.current?.val ?? [])];
        const index = list.findIndex(item => item.id === clientId);

        if (index >= 0) {
            list[index].error = err?.isAxiosError
            ? t('errorUploadingFile')
            : err?.message ?? t('errorUploadingFile');
            list[index].processing = false;
        }

        syncProgress(list);
        clearUploadOp(clientId);

        throw new Error(
            err?.isAxiosError ? t('errorUploadingFile') : (err?.message ?? t('errorUploadingFile'))
        );
        }
    },
    [t, userData.post_preview_type]
    );

    const uploadFiles = (files: File[]) => {
        const placeholders = files.map(file => {
            const clientId = Math.random().toString(36).slice(2)
            ensureUploadOp(clientId)
            return {
                id: clientId,
                client_id: clientId,
                percentage: 0,
                fileName: file.name,
                previewOn: true,
                type: file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'photo' : file.type.startsWith('audio/') ? 'audio' : 'other',
                processing: true,
                previewUrl: URL.createObjectURL(file),
                error: '',
                locked: !!priceRef.current
            } as any
        })

        const next = [...(progressInfosRef.current?.val ?? progressInfos.val ?? []), ...placeholders]
        syncProgress(next)
        setProgressInfosNew({ val: next })
        files.forEach((file, index) => { void upload(file, String(placeholders[index].id)) })
    }

    const uploadCustomVideoPreview = async (file: File, signal?: AbortSignal): Promise<string> => {
        const meta = await import('helpers/video/readClientMeta')
            .then(async ({ readClientVideoMeta }) => await readClientVideoMeta(file))
            .catch(() => null)
        const duration = meta?.duration || 0
        const orientation = meta?.orientation
        const multipart = await startVideoMultipart({ type: 'post_video', contentType: file.type, size: file.size }, signal)
        const { key, uploadId, partSize, totalParts, parts } = multipart

        try {
            const completedParts = await uploadVideoMultipart({
                file, key, uploadId, partSize, totalParts, parts,
                contentType: file.type,
                concurrency: 4,
                signal,
                setProgress: () => {}
            })
            if (!completedParts?.length) throw new Error('Preview video upload did not complete.')
            await completeVideoMultipart({ key, uploadId, parts: completedParts }, signal)

            const response = await attachVideoFromS3({
                s3_key: key,
                duration: duration > 0 ? Number(duration.toFixed(2)) : null,
                orientation: orientation ?? null,
                preview: true,
                is_preview_video: true,
                type: 'post'
            } as any, signal)
            const data = response?.data?.data ?? response?.data ?? response
            if (typeof data?.id !== 'string' || !data.id) throw new Error('Preview video did not attach.')
            return data.id
        } catch (error) {
            try { await abortVideoMultipart({ key, uploadId }) } catch {}
            throw error
        }
    }

    const uploadCustomPreviewThumbnail = async (
        file: File,
        videoId: string
    ): Promise<string> => {
        const { url: putUrl, key } = await presignPhoto({
            type: 'preview_cover',
            contentType: file.type,
            size: file.size
        })

        await uploadFileToS3(putUrl, file, () => {})

        const response = await attachPreviewCoverFromS3({
            video_id: videoId,
            s3_key: key,
            expected_bytes: file.size
        })

        const data =
            response?.data?.data ??
            response?.data ??
            response

        if (typeof data?.key !== 'string' || !data.key) {
            throw new Error('Preview cover did not attach.')
        }

        return data.key
    }


    const uploadCustomVideoThumbnail = async (file: File): Promise<string> => {
        const { url: putUrl, key } = await presignPhoto({
            type: 'post_image',
            contentType: file.type,
            size: file.size
        })
        await uploadFileToS3(putUrl, file, () => {})
        return key
    }

    const handleEditVideoPreview = (
        videoId: string,
        defaultPreview: string,
        storageSrc: string,
        duration?: number,
        orientation?: string,
        thumbnails?: string[],
        thumbnailTimes?: Array<number | null>,
        imagePreview?: string,
        trimPreview?: { start: number; end: number },
        thumbType: 'clip' | 'thumb' = 'thumb',
        customThumbnailUrl?: string | null,
        customThumbnailTime?: number | null,
        customPreviewUrl?: string | null,
        previewVideoId?: string | null,
        activeThumbnailIndex?: number | null,
        thumbsQueryDone = true,
        scrubFrames?: ClientFrame[],
        previewThumbnailIndex?: number | null,
        customPreviewThumbKey?: string | null,
        customPreviewThumbnailUrl?: string | null,
        storyboard?: StoryboardData | null,
        cloudfront?: CloudFrontCredentials | null,
        customPreviewCloudfront?: CloudFrontCredentials | null
    ) => {
        const normalizedCustomThumbnailTime =
            customThumbnailTime != null &&
            Number.isFinite(Number(customThumbnailTime))
                ? Number(customThumbnailTime)
                : null

        const hasCustomThumbnail =
            typeof customThumbnailUrl === 'string' &&
            customThumbnailUrl.trim() !== '' &&
            normalizedCustomThumbnailTime !== null

        let thumbnailIndex: number | undefined

        if (
            !hasCustomThumbnail &&
            typeof activeThumbnailIndex === 'number' &&
            thumbnails?.length
        ) {
            thumbnailIndex = activeThumbnailIndex
        } else if (
            !hasCustomThumbnail &&
            imagePreview &&
            thumbnails?.length
        ) {
            const index = thumbnails.indexOf(imagePreview)
            if (index >= 0) thumbnailIndex = index
        }

        setVideoPreviewData({
            videoId,
            defaultPreview,
            storageSrc,
            cloudfront,
            customPreviewCloudfront,
            duration,
            orientation,
            thumbnails,
            thumbnailTimes,
            trimPreview,
            thumbnailIndex,
            thumbType,
            thumbsQueryDone,
            customThumbnailUrl: hasCustomThumbnail
                ? customThumbnailUrl
                : null,
            customThumbnailTime: hasCustomThumbnail
                ? normalizedCustomThumbnailTime
                : null,
            customPreviewUrl: customPreviewUrl ?? null,
            scrubFrames,
            previewVideoId: previewVideoId ?? null,
            previewThumbnailIndex: previewThumbnailIndex ?? null,
            customPreviewThumbKey: customPreviewThumbKey ?? null,
            customPreviewThumbnailUrl:
                customPreviewThumbnailUrl ?? null,
            storyboard,
        })

        setEditPreviewModalOpen(true)
    }

const handleUpdatePreview = (data: ConfirmPayload) => {
    const currentList =
        (progressInfosRef.current?.val ?? progressInfos.val)

    const nextList = produce(currentList, draft => {
        const item = draft.find(
            info => info.previewUrl === data.defaultPrev
        ) as any

        if (!item) return

        if (data.activeThumbnail) {
            item.imagePreview = data.activeThumbnail
        }

        item.thumbType = data.thumbType

        const selectedCustomThumbnail =
            typeof data.selectedTime === 'number'

        const selectedGeneratedThumbnail =
            typeof data.selectedIndex === 'number'

        const selectedUploadedThumbnail =
            typeof data.customThumbKey === 'string' &&
            data.customThumbKey.length > 0

        if (selectedUploadedThumbnail) {
            item.custom_thumb_key = data.customThumbKey
            item.custom_thumb_crop =
                data.customThumbCrop ?? null
            item.custom_thumbnail = null
            item.customThumbnailUrl = null
            item.activeThumbnailIndex = null
        } else if (selectedCustomThumbnail) {
            item.custom_thumbnail =
                Number(data.selectedTime)
            item.customThumbnailUrl =
                data.activeThumbnail ?? null
            item.custom_thumb_key = null
            item.custom_thumb_crop = null
            item.activeThumbnailIndex = null
        } else if (selectedGeneratedThumbnail) {
            item.activeThumbnailIndex =
                Number(data.selectedIndex)
            item.custom_thumbnail = null
            item.customThumbnailUrl = null
            item.custom_thumb_key = null
            item.custom_thumb_crop = null
        } else if (
            data.activeThumbnail &&
            Array.isArray(item.thumbnails)
        ) {
            const derivedIndex =
                item.thumbnails.indexOf(data.activeThumbnail)

            if (derivedIndex >= 0) {
                item.activeThumbnailIndex =
                    derivedIndex
                item.custom_thumbnail = null
                item.customThumbnailUrl = null
                item.custom_thumb_key = null
                item.custom_thumb_crop = null
            }
        }

        if (data.customPreviewThumbKey) {
            item.previewThumbIndex = null
            item.previewThumbKey =
                data.customPreviewThumbKey
            item.previewThumbUrl =
                data.customPreviewThumbnailUrl ?? null
        } else if (
            typeof data.previewThumbnailIndex === 'number'
        ) {
            item.previewThumbIndex =
                data.previewThumbnailIndex
            item.previewThumbKey = null
            item.previewThumbUrl =
                item.thumbnails?.[
                    data.previewThumbnailIndex
                ] ?? null
        } else {
            item.previewThumbIndex = null
            item.previewThumbKey = null
            item.previewThumbUrl = null
        }

        item.preview_video_id =
            data.previewVideoId ?? null

        item.customPreviewUrl =
            data.customPreviewUrl ?? null

        item.trimPreview =
            !data.previewVideoId &&
            data.thumbType === 'clip' &&
            data.clip
                ? {
                    start: data.clip.start,
                    end: data.clip.end
                }
                : undefined

        item.thumbnailChanged = true
    })

    syncProgress(nextList)
    setProgressInfosNew({ val: nextList })
    setPostDirty(true)
    setEditPreviewModalOpen(false)
}

    const handleToggleMediaLocked = (idx: number) => {
    const updatedProgressInfos = [...progressInfos.val];
    const updatedSelectedFiles = [...selectedFiles];

    const currentItem = updatedProgressInfos[idx];
    const isCurrentlyLocked = !!currentItem.locked;

    if (post.price && isCurrentlyLocked) {
        const lockedCount = updatedProgressInfos.filter(item => item.locked).length;
        if (lockedCount === 1) {
        addToast('error', 'At least one attachment must remain locked.');
        return;
        }
    }

    updatedProgressInfos[idx] = {
        ...currentItem,
        locked: !isCurrentlyLocked
    };

    const isNowUnlocked = isCurrentlyLocked;

    if (isNowUnlocked && idx > 0) {
        const everyOtherItemLocked = updatedProgressInfos.every((item, i) =>
        i === idx ? !item.locked : !!item.locked
        );

        if (everyOtherItemLocked) {
        const [movedInfo] = updatedProgressInfos.splice(idx, 1);
        const [movedFile] = updatedSelectedFiles.splice(idx, 1);

        updatedProgressInfos.unshift(movedInfo);
        if (movedFile) updatedSelectedFiles.unshift(movedFile);
        }
    }

    syncProgress(updatedProgressInfos)
    setProgressInfosNew({ val: updatedProgressInfos });
    setSelectedFiles(updatedSelectedFiles);

    if (!postDirty) setPostDirty(true);
    };

    const handleTogglePreviewOn = (idx: number) => {
    const cur = [...(progressInfosRef.current?.val ?? progressInfos.val)];
    const next = cur.map((info, index) =>
        index === idx ? { ...info, previewOn: !info.previewOn } : info
    );

    syncProgress(next);
    setProgressInfosNew({ val: next });

    if (!postDirty) setPostDirty(true);
    };

    const isNewUpload = (item: IProgressInfo) =>
        !!item.client_id || (item as any).source === 'vault'

    const isPersistedMedia = (item: IProgressInfo) =>
        (item as any).source !== 'vault' &&
        !item.client_id &&
        !!item.id

    const getOrder = (item: IProgressInfo) => {
        const itemId = getId(item)

        const idx = progressInfos.val.findIndex(
            current => getId(current) === itemId
        )

        return idx >= 0 ? idx + 1 : 1
    }

    const preparePost = (
    progInfos: IProgressInfo[]
    ) => {
    const photos = progInfos
        .filter(item => item.type === 'photo')
        .filter(isNewUpload)
        .map(item => ({
            photo_id: (item as any).photo_id,
            is_duplicate: !!(item as any).is_duplicate,
            hash_image: (item as any).hash_image ?? null,
            path: item.storageUrl || item.previewUrl,
            order: getOrder(item),
            orientation: item.orientation,
            locked: item.locked,
            text: item.text
                ? {
                    value: item.text.value ?? null,
                    x: item.text.x ?? null,
                    y: item.text.y ?? null,
                }
                : null,
        }))
    /*
    * New uploads only.
    *
    * Existing videos are updated separately through
    * video_thumbnail_updates.
    */
const videos = progInfos
    .filter(item => item.type === 'video')
    .filter(isNewUpload)
    .map(item => ({
        video_id: (item as any).video_id,
        path: item.storageUrl || item.previewUrl,
        order: getOrder(item),
        duration:
            typeof item.duration === 'number'
                ? item.duration
                : null,
        orientation: item.orientation ?? null,
        locked: item.locked,
        preview: !!item.previewOn,
        active_thumbnail:
            (item as any).activeThumbnailIndex ?? null,
        custom_thumb_key:
            (item as any).custom_thumb_key ?? null,
        custom_thumb_crop:
            (item as any).custom_thumb_crop ?? null,
        custom_thumbnail:
            (item as any).custom_thumbnail ?? null,
        preview_thumb:
            (item as any).previewThumbIndex ?? null,
        preview_thumb_key:
            (item as any).previewThumbKey ?? null,
        preview_video_id:
            (item as any).preview_video_id ?? null,
        video_clip:
            item.previewOn &&
            !(item as any).preview_video_id &&
            (item as any).trimPreview
                ? {
                    start: (item as any).trimPreview.start,
                    end: (item as any).trimPreview.end
                }
                : null,
        hash_video:
            (item as any).hash_video ?? null,
    }))
const video_thumbnail_updates = progInfos
    .filter(item => item.type === 'video')
    .filter(isPersistedMedia)
    .filter(item => (item as any).thumbnailChanged === true)
    .map(item => ({
        video_id: String(item.id),
        thumbnail_index:
            typeof (item as any).activeThumbnailIndex === 'number'
                ? Number((item as any).activeThumbnailIndex)
                : null,
        custom_thumb_key:
            (item as any).custom_thumb_key ?? null,
        custom_thumb_crop:
            (item as any).custom_thumb_crop ?? null,
        custom_thumbnail:
            (item as any).custom_thumbnail ?? null,
        preview_thumb:
            (item as any).previewThumbIndex ?? null,
        preview_thumb_key:
            (item as any).previewThumbKey ?? null,
        preview_video_id:
            (item as any).preview_video_id ?? null,
        video_clip:
            item.previewOn &&
            !(item as any).preview_video_id &&
            (item as any).trimPreview
                ? {
                    start: (item as any).trimPreview.start,
                    end: (item as any).trimPreview.end
                }
                : null,
    }))

    const sounds = progInfos
        .filter(
        item =>
            item.type === 'sound' ||
            item.type === 'audio'
        )
        .filter(isNewUpload)
        .map(item => ({
        path: item.storageUrl || item.previewUrl,
        order: getOrder(item),
        locked: item.locked,
        }))

    const existing_media_updates = progInfos
        .filter(isPersistedMedia)
        .map(item => ({
            type:
                item.type === 'audio'
                    ? 'sound'
                    : item.type,

            id: String(item.id),
            order: getOrder(item),
            locked: Boolean(item.locked),

        ...(item.type === 'photo'
            ? {
                text:
                    typeof item.text === 'object'
                        ? {
                            value: item.text?.value ?? null,
                            x: item.text?.x ?? null,
                            y: item.text?.y ?? null,
                        }
                        : item.text
                            ? {
                                value: item.text,
                                x: null,
                                y: null,
                            }
                            : null,
            }
            : {}),

            ...(item.type === 'video'
                ? {
                    preview: Boolean(item.previewOn),
                    active_thumbnail:
                        (item as any).activeThumbnailIndex ?? null,
                    custom_thumb_key:
                        (item as any).custom_thumb_key ?? null,
                    custom_thumb_crop:
                        (item as any).custom_thumb_crop ?? null,
                    custom_thumbnail:
                        (item as any).custom_thumbnail ?? null,
                    preview_thumb:
                        (item as any).previewThumbIndex ?? null,
                    preview_thumb_key:
                        (item as any).previewThumbKey ?? null,
                    preview_video_id:
                        (item as any).preview_video_id ?? null,
                    video_clip:
                        item.previewOn &&
                        !(item as any).preview_video_id &&
                        (item as any).trimPreview
                            ? {
                                start: (item as any).trimPreview.start,
                                end: (item as any).trimPreview.end
                            }
                            : null,
                }
                : {}),
        }))

    return {
        body: post.body,
        photos,
        videos,
        sounds,

        video_thumbnail_updates,

        /*
        * Existing attachments are not included in photos/videos/sounds
        * because those arrays are only for newly uploaded media.
        *
        * Send persisted-media changes separately.
        */
        existing_media_updates,

        price: post.price,

        categories:
            extractCategoryIds(selectedCategories),

        tags: post.tags,
        schedule_date: post.schedule_date,
        schedule_timezone: post.schedule_date
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null,
        shareOnTwitter: post.shareOnTwitter,
        media_to_delete: {
            photos: Array.from(new Set(mediaToDelete.photos)),
            videos: Array.from(new Set(mediaToDelete.videos)),
            sounds: Array.from(new Set(mediaToDelete.sounds)),
        },

        /*
        * You can leave reorder temporarily for backend compatibility.
        * existing_media_updates now also contains order.
        */
        reorder: progInfos
            .filter(isPersistedMedia)
            .map(item => ({
                type:
                    item.type === 'audio'
                        ? 'sound'
                        : item.type,

                id: String(item.id),
                order: getOrder(item),
            })),
    }
    }

    const handlePollSubmit = async (pollData: IPoll) => {
        try {
            const bgUploadedSrc =
                (pollData as any).pollBgUploadedSrc as string | null | undefined

            const bgImage =
                bgUploadedSrc ||
                (typeof pollData.pollBgImg === 'string' ? pollData.pollBgImg : null) ||
                (typeof pollData.pollBg === 'string' && pollData.pollBg.startsWith('http')
                    ? pollData.pollBg
                    : null)

            const payload = {
                type: pollData.type,
                question: pollData.question,
                groups: extractGroupIds(postAudience),
                categories: extractCategoryIds(selectedCategories),
                answers: pollData.answers,
                background_color: bgImage ? null : pollData.pollBg,
                background_src: bgImage,
                schedule_timezone: post.schedule_date
                    ? Intl.DateTimeFormat().resolvedOptions().timeZone
                    : null,
            }

            await editPoll(payload, postId)

            await queryClient.invalidateQueries('getFeed')
            await queryClient.invalidateQueries(['getSinglePost', postId])

            navigate(`/${userData?.username}`)
        } catch (err) {
            console.error(err)
            addToast('error', t('error:errorSomethingWentWrong'))
        }
    }

    const submitPost = async () => {
        setPostUploading(true)

        try {
            if (pollData) {
                await handlePollSubmit(pollData)
                return
            }

            if (goalData) {
                return
            }

            const currentPostData = preparePost(progressInfos.val)
            const post = preparePost(progressInfosNew.val)

            const hasBody = !!post.body?.trim()

            const hasPhotoOrVideo = progressInfos.val.some(item => {
                if (item.type === 'photo') {
                    return !mediaToDelete.photos.includes(String(item.id))
                }

                if (item.type === 'video') {
                    return !mediaToDelete.videos.includes(String(item.id))
                }

                return false
            })

            const hasAnyMedia = progressInfos.val.some(item => {
                if (item.type === 'photo') {
                    return !mediaToDelete.photos.includes(String(item.id))
                }

                if (item.type === 'video') {
                    return !mediaToDelete.videos.includes(String(item.id))
                }

                if (item.type === 'sound' || item.type === 'audio') {
                    return !mediaToDelete.sounds.includes(String(item.id))
                }

                return false
            })

            if (!hasBody && !hasAnyMedia && !pollData && !goalData) {
                addToast('error', t('validation:postCannotBeEmpty'))
                return
            }

            if (currentPostData.price && !hasBody && !hasPhotoOrVideo) {
                addToast('error', t('validation:premiumPostCannotBeAudioOnly'))
                return
            }

            const editResponse = await editPost(
            post,
            postId
            )

            const updatedPost =
            editResponse?.data?.data ??
            editResponse?.data ??
            editResponse

            if (updatedPost?.id) {
            updatePostCache(updatedPost)

            queryClient.setQueriesData(
                {
                predicate: query =>
                    isPostQueryKey(
                    query.queryKey
                    )
                },
                cached =>
                updatePostInQueryData(
                    cached,
                    updatedPost
                )
            )
            }

            navigate(`/${userData.username}`)
        } catch (err) {
            console.error(err)
            addToast('error', t('error:errorSomethingWentWrong'))
        } finally {
            setPostUploading(false)
        }
    }
    const extractGroupIds = (groups: IGroup[]) => {
        const groupIds = groups.map(group => {
            return group.id
        })

        return groupIds || []
    }

    const extractCategoryIds = (categories: ICategory[]) =>
        categories
            .map(category => String(category.id))
            .filter(Boolean)

    const renderContent = () => {
        if (pollData) {
            return (
                <PollPreview
                    pollData={pollData}
                    clearPollData={() => {
                        setPostType('')
                        setPollData(null)
                    }}
                />
            )
        } else if (goalData) {
            return (
                <GoalPreview
                    goalData={goalData}
                    clearGoalData={() => {
                        setPostType('')
                        setGoalData(null)
                    }}
                />
            )
        } else {
            return (
                <PostText
                    body={post.body}
                    tags={post.tags}
                    setText={(body: string, tags: ITag[]) => {
                        setPost({ ...post, body, tags })
                    }}
                />
            )
        }
    }

    if (addPollOpen) {
        const portal = document.getElementById('portal')

        if (!portal) return null
        !portal.classList.contains('portal--open') &&
            portal.classList.add('portal--open')
        return createPortal(
            <AddPoll
                handleClose={() => {
                    portal.classList.contains('portal--open') &&
                        portal.classList.remove('portal--open')
                    setAddPollOpen(false)
                }}
                handleSetPollData={(data: IPoll) => {
                    setPostType('poll')
                    setPollData(data)
                }}
            />,
            portal
        )
    }
    if (addGoalOpen) {
        const portal = document.getElementById('portal')

        if (!portal) return null
        !portal.classList.contains('portal--open') &&
            portal.classList.add('portal--open')
        return createPortal(
            <AddGoal
                handleClose={() => {
                    portal.classList.contains('portal--open') &&
                        portal.classList.remove('portal--open')
                    setAddGoalOpen(false)
                }}
                handleSetGoalData={(data: IGoal) => {
                    setPostType('goal')
                    setGoalData(data as any)
                    portal.classList.contains('portal--open') &&
                        portal.classList.remove('portal--open')
                    setAddGoalOpen(false)
                }}
                initialBackgroundImage={null}
                initialBackgroundFile={null}
                initialBackgroundUploadedSrc={null}
                initialBackgroundOrientation={''}
            />,
            portal
        )
    }

    return (
        <>
            {editPreviewModalOpen && videoPreviewData && (
                <EditPreviewModal
                    defaultPreview={videoPreviewData.defaultPreview}
                    cloudfront={videoPreviewData.cloudfront}
                    customPreviewCloudfront={videoPreviewData.customPreviewCloudfront}
                    storageSrc={videoPreviewData.storageSrc}
                    duration={videoPreviewData.duration}
                    orientation={videoPreviewData.orientation}
                    thumbnails={videoPreviewData.thumbnails}
                    thumbnailTimes={videoPreviewData.thumbnailTimes}
                    scrubFrames={videoPreviewData.scrubFrames}
                    storyboard={videoPreviewData.storyboard}
                    thumbnailIndex={videoPreviewData.thumbnailIndex}
                    previewThumbnailIndex={
                        videoPreviewData.previewThumbnailIndex
                    }
                    customPreviewThumbKey={
                        videoPreviewData.customPreviewThumbKey
                    }
                    customPreviewThumbnailUrl={
                        videoPreviewData.customPreviewThumbnailUrl
                    }
                    trimPreview={videoPreviewData.trimPreview}
                    thumbType={videoPreviewData.thumbType}
                    customThumbnailUrl={
                        videoPreviewData.customThumbnailUrl
                    }
                    customThumbnailTime={
                        videoPreviewData.customThumbnailTime
                    }
                    customPreviewUrl={
                        videoPreviewData.customPreviewUrl
                    }
                    previewVideoId={
                        videoPreviewData.previewVideoId
                    }
                    thumbsQueryDone={
                        videoPreviewData.thumbsQueryDone
                    }
                    initialPreviewOn={
                        videoPreviewData.thumbType
                            ? videoPreviewData.thumbType === 'clip'
                            : userData?.post_preview_type === 'video'
                    }
                    uploadCustomThumbnail={
                        uploadCustomVideoThumbnail
                    }
                    uploadCustomPreviewThumbnail={async file =>
                        await uploadCustomPreviewThumbnail(
                            file,
                            videoPreviewData.videoId
                        )
                    }
                    uploadCustomPreview={
                        uploadCustomVideoPreview
                    }
                    cancelCb={() =>
                        setEditPreviewModalOpen(false)
                    }
                    confirmCb={handleUpdatePreview}
                />
            )}
            {recorderType === 'video' && (
                <MediaRecorder
                    type={recorderType}
                    status={status}
                    handleClose={() => {
                        setRecorderType('')
                    }}
                    setRecordedChunks={data => {
                        setRecordedChunks(data)
                    }}
                    handleAudioStart={() => {
                        startRecording()
                    }}
                    handleAudioStop={stopRecording}
                />
            )}
            {recorderType === 'audio' && (
                <AudioRecorder
                    type={recorderType}
                    status={audioRecording}
                    handleClose={() => {
                        setRecorderType('')
                    }}
                    handleAudioStart={() => {
                        startAudioRecording()
                    }}
                    handleAudioStop={stopAudioRecording}
                />
            )}
            <NewPostContainer
                className={`newpost newpost--edit ${
                    pollData ?? goalData ? 'newpost--pollPreview' : ''
                }`}
                $editPreviewModalOpen={editPreviewModalOpen}
            >
                {previewOpen && (
                    <PostModal
                        postId={previewMedia}
                        newPostPage={true}
                        closeFn={() => {
                            setPreviewOpen(false)
                        }}
                    />
                )}
                <PostVisibility
                    isEdit={!isScheduledPost}
                    audience={postAudience}
                    groups={groups}
                    setAudience={(val: IGroup[]) => {
                        setPostAudience(val)
                        postAudienceRef.current = val
                        setPostDirty(true)
                    }}
                />

                <div className={'newpost__body'}>
                    {renderContent()}
                    {progressInfos.val.map(
                        (item: IProgressInfo, key: number) => {
                            return (
                                (item.type === 'sound' ||
                                    item.type === 'audio') && (
                                    <AudioMessage
                                        key={key}
                                        customClass={`newpost__audiomessage ${
                                            audioOnly
                                                ? ''
                                                : 'newpost__audiomessage--active'
                                        }`}
                                        audioBlob={item.previewUrl}
                                        audioReady={
                                            progressInfos.val[key] &&
                                            !!progressInfos.val[key].previewUrl
                                        }
                                        waveColor='gray'
                                        waveBackgroundColor='transparent'
                                        processing={
                                            progressInfos.val[key] &&
                                            progressInfos.val[key].percentage >
                                                0 &&
                                            progressInfos.val[key].percentage <
                                                100
                                        }
                                        percentage={
                                            progressInfos.val?.[key]?.percentage
                                        }
                                        uploadError={
                                            progressInfos.val?.[key]?.error
                                        }
                                        hasDeleteBtn={true}
                                        deleteFn={() => {
                                            removeByUrl(item.previewUrl)
                                            progressInfos.val[key] &&
                                                progressInfos.val[key].cancel()
                                        }}
                                    />
                                )
                            )
                        }
                    )}
                    {selectedTaggedCreators.length > 0 && (
                    <div className="newpost__selectedTagsRow">
                        <button
                        type="button"
                        className="newpost__tagCreatorsButton"
                        onClick={() => setTagCreatorsOpen(prev => !prev)}
                        >
                        <div className="newpost__tagCreatorsButton__avatars">
                            {selectedTaggedCreators.slice(0, 3).map((creator, index) => (
                            <div
                                key={creator.id}
                                className="newpost__tagCreatorsButton__avatar"
                                style={{
                                marginLeft:
                                    index === 0
                                    ? 0
                                    : index === 1
                                    ? '-0.75rem'
                                    : '-0.5rem',
                                zIndex: index + 1
                                }}
                            >
                                <img
                                src={creator.avatarUrl}
                                alt={creator.display}
                                className={
                                    creator.public_tag
                                    ? ''
                                    : 'newpost__tagCreatorsButton__avatar--private'
                                }
                                />
                            </div>
                            ))}
                        </div>

                        <span>
                            {selectedTaggedCreators.length === 1
                            ? selectedTaggedCreators[0].display
                            : `${selectedTaggedCreators.length} Tags`}
                        </span>
                        </button>
                    </div>
                    )}
                    <PostMediaInputs
                        filesAdded={
                            (!(progressInfos.val.length === 0) && !audioOnly) ||
                            !!pollData ||
                            !!goalData
                        }
                        handleSetUpload={selectFiles}
                        toggleReorder={() => {
                            setReorderActive(!reorderActive)
                        }}
                        appendAttachments={appendAttachments}
                        handleAddPollOpen={() => {
                            setAddPollOpen(true)
                        }}
                        handleAddGoalOpen={() => {
                            setAddGoalOpen(true)
                        }}
                        reorderActive={reorderActive}
                        confirmReorder={handleConfirmReorder}
                        postType={postType}
                        handleOpenRecorder={(type: string) => {
                            setRecorderType(type)
                        }}
                        hasAudio={hasAudio}
                        selectedFiles={selectedFiles}
                        setSelectedFiles={setSelectedFiles}
                        setProgressInfos={setProgressInfos}
                        progressInfos={progressInfos}
                        hasOnlyPhotos={
                            progressInfos.val.length > 0 &&
                            progressInfos.val.every(item => item.type === 'photo')
                        }
                        hasBlockingMediaForPollGoal={
                            progressInfos.val.some(
                                item =>
                                    item.type === 'video' ||
                                    item.type === 'audio' ||
                                    item.type === 'sound'
                            )
                        }
                        selectedTaggedCreators={selectedTaggedCreators}
                        setSelectedTaggedCreators={setSelectedTaggedCreators}
                        tagCreatorsOpen={tagCreatorsOpen}
                        setTagCreatorsOpen={setTagCreatorsOpen}
                    />
                    {!pollData && !goalData && (
                        <div
                            className={`newpost__body__media ${
                                !(progressInfos.val.length === 0) && !audioOnly
                                    ? 'newpost__body__media--active'
                                    : ''
                            }`}
                        >
                            <div className="newpost__body__media__wrapper" ref={dropRef}>
                                {progressInfos.val.map((item: IProgressInfo, key: number) => {
                                    const id = getId(item)

                                    if (item.type === 'photo') {
                                        return (
                                            <div
                                                key={id}
                                                data-id={id}
                                                className="drop-card drop-card--image"
                                            >
                                                <PostImage
                                                    isEdit={true}
                                                    id={key}
                                                    imagePreview={item.imagePreview}
                                                    processing={!!item.processing}
                                                    percentage={item.percentage ?? 0}
                                                    abortUploadCb={() => removeMedia(item)}
                                                    uploadError={item.error}
                                                    previewUrl={item.previewUrl}
                                                    locked={item.locked}
                                                    isPremium={!!post.price}
                                                    removeFile={() => removeMedia(item)}
                                                    removeByUrl={() => removeByUrl(item.previewUrl)}
                                                    editPreview={() => {
                                                        const editablePhotoUrl =
                                                            getCachedBlob(item.previewUrl) ??
                                                            item.previewUrl

                                                        console.log('[PHOTO EDIT SOURCE]', {
                                                            original: item.previewUrl,
                                                            cached: getCachedBlob(item.previewUrl),
                                                            using: editablePhotoUrl
                                                        })

                                                        modalContext.addModal(
                                                            '',
                                                            <PhotoEditView
                                                                photo={{
                                                                    url: editablePhotoUrl,
                                                                    text: item.text
                                                                }}
                                                                handleClose={() => modalContext.clearModal()}
                                                                handleSetPhotoData={(data) => {
                                                                    const next = produce(
                                                                        progressInfosRef.current?.val ?? progressInfos.val,
                                                                        draft => {
                                                                            const target = draft.find(
                                                                                media => getId(media) === getId(item)
                                                                            )

                                                                            if (target) {
                                                                                target.text = data.text
                                                                            }
                                                                        }
                                                                    )

                                                                    syncProgress(next)
                                                                    setProgressInfosNew({ val: next })
                                                                    setPostDirty(true)
                                                                }}
                                                            />,
                                                            true,
                                                            true,
                                                            'fullScreen no-close'
                                                        )
                                                    }}
                                                    handleToggleMediaLocked={handleToggleMediaLocked}
                                                />
                                            </div>
                                        )
                                    }

                                    if (item.type === 'video') {
                                        return (
                                            <div
                                                key={id}
                                                data-id={id}
                                                className="drop-card drop-card--video"
                                            >
                                                <PostVideo
                                                    setPreviewOpen={setPreviewOpen}
                                                    setPreviewMedia={setPreviewMedia}
                                                    mediaDta={item.exif_video_media}
                                                    isEdit={true}
                                                    id={key}
                                                    processing={!!item.processing}
                                                    percentage={item.percentage ?? 0}
                                                    thumbsReady={
                                                    !!item.imagePreview ||
                                                    (item as any).thumbsDone === true
                                                    }
                                                    abortUploadCb={() => removeMedia(item)}
                                                    uploadError={item.error}
                                                    previewUrl={item.previewUrl}
                                                    imagePreview={item.imagePreview}
                                                    locked={item.locked}
                                                    isPremium={!!post.price}
                                                    removeFile={() => removeMedia(item)}
                                                    removeByUrl={() => removeByUrl(item.previewUrl)}
                                                    editPreview={() => {
                                                        const resolvedThumbType: 'clip' | 'thumb' =
                                                            (item as any).thumbType === 'clip' ? 'clip' : 'thumb'

                                                            handleEditVideoPreview(
                                                                String(item.id),
                                                                item.previewUrl,
                                                                item.storageUrl,
                                                                item.duration,
                                                                item.orientation,
                                                                item.thumbnails,
                                                                item.thumbTimes,
                                                                item.imagePreview,
                                                                item.trimPreview,
                                                                resolvedThumbType,
                                                                (item as any).customThumbnailUrl ?? null,
                                                                (item as any).custom_thumbnail != null
                                                                    ? Number(item.custom_thumbnail)
                                                                    : null,
                                                                (item as any).customPreviewUrl ?? null,
                                                                (item as any).preview_video_id ?? null,
                                                                (item as any).activeThumbnailIndex ?? null,
                                                                (item as any).thumbsQueryDone ?? true,
                                                                (item as any).scrubFrames ?? [],
                                                                (item as any).previewThumbIndex ?? null,
                                                                (item as any).previewThumbKey ?? null,
                                                                (item as any).previewThumbUrl ?? null,
                                                                item.storyboard ?? null,
                                                                (item as any).cloudfront ?? null,
                                                                (item as any).customPreviewCloudfront ?? null
                                                            )
                                                    }}
                                                    previewOn={item.previewOn}
                                                    handleToggleMediaLocked={handleToggleMediaLocked}
                                                    handleTogglePreviewOn={handleTogglePreviewOn}
                                                />
                                            </div>
                                        )
                                    }

                                    return null
                                })}

                                {!reorderActive && progressInfos.val.length > 0 && !audioOnly && (
                                    <div className="drop-card add-anything-card">
                                        <AddAnything handleSetUpload={selectFiles} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <PostInputs
                        isEditView={true}
                        isUploading={postUploading}
                        price={post.price}
                        hasCategories={!(selectedCategories.length === 0)}
                        setPrice={(val: number) => {
                            setPost(prev => ({ ...prev, price: val }));

                            const cur = (progressInfosRef.current?.val ?? progressInfos.val);

                            const next = cur.map(info => ({
                                ...info,
                                locked: !!val
                            }));

                            syncProgress(next);
                            setProgressInfosNew({ val: next });
                        }}
                        submitPost={submitPost}
                        postReady={postReady && postDirty}
                        updateDate={(val: Date) => {
                            const now = new Date()
                            if (val < now) {
                                addToast('error', t('error:errorInvalidDate'))
                            } else {
                                setPost({ ...post, schedule_date: val })
                            }
                        }}
                        scheduleDate={post.schedule_date}
                        removeSchedule={() => {
                            setPost({ ...post, schedule_date: null })
                        }}
                        addCategories={handleOpenCategories}
                        userShareOnTwitter={post.shareOnTwitter as boolean}
                        toggleShareOnTwitter={(share: boolean) => {
                            setPost({
                                ...post,
                                shareOnTwitter: share
                            })
                        }}
                    />
                </div>
            </NewPostContainer>
        </>
    )
}

interface NewPostContainerProps {
    $editPreviewModalOpen: boolean
}

const NewPostContainer = styled.div<NewPostContainerProps>`
    ${props => props.$editPreviewModalOpen && 'display: none;'}
`
